import Ajv from "ajv";
import * as R from "ramda";

//#region rolldown:runtime
var __defProp = Object.defineProperty;
var __exportAll = (all, symbols) => {
	let target = {};
	for (var name in all) {
		__defProp(target, name, {
			get: all[name],
			enumerable: true
		});
	}
	if (symbols) {
		__defProp(target, Symbol.toStringTag, { value: "Module" });
	}
	return target;
};

//#endregion
//#region src/ajv-extensions.js
var ajv_extensions_exports = /* @__PURE__ */ __exportAll({
	createValidator: () => createValidator,
	processErrors: () => processErrors
});
const createValidator = (schema, ...referenceSchemas) => {
	const checkForSchemaTitle = (s) => throwUnless(R.has("title", s), internalError(`Schema ${s.$id} has no title`));
	checkForSchemaTitle(schema);
	referenceSchemas.map(checkForSchemaTitle);
	const ajv = new Ajv({
		allErrors: true,
		verbose: true,
		useDefaults: true,
		extendRefs: "fail"
	});
	referenceSchemas.forEach((s) => ajv.addSchema(s));
	const validate = ajv.compile(schema);
	return (data) => validate(data) ? void 0 : processErrors(validate.errors);
};
const internalError = (s, found) => `AjvExtensions error: ${s}; found: ${JSON.stringify(found)}`;
const throwUnless = (b, errorMessage) => {
	if (!b) throw new Error(errorMessage);
	return b;
};
const ajvErrorAsString = (error, padLength) => {
	const ellipses = (s) => s.length > 100 ? `${s.substring(0, 100)}...` : s;
	const errorString = (dataPath$1, message$1, actual, params$1 = "") => `    ${dataPath$1} ${message$1} ${params$1}; found ${ellipses(JSON.stringify(actual))}`;
	const nameOfIfClause = () => {
		const result = R.path(["schema", "$ref"], error);
		throwUnless(result, internalError(`"if" doesn't include a "$ref"`, parentSchema));
		throwUnless(result.match(/.*\/is/), internalError(`"if" doesn't include "is" in its name"`, parentSchema));
		return result.replace(/.*\/is/, "");
	};
	const { message, params, keyword, data, parentSchema } = error;
	const dataPath = error.schemaPath.padEnd(padLength);
	switch (keyword) {
		case "required": return errorString(dataPath, `A "${parentSchema.title}" must have property "${params.missingProperty}"`, data);
		case "additionalProperties": return errorString(dataPath, `A "${parentSchema.title}" may not have property "${params.additionalProperty}"`, data);
		case "if": return errorString(dataPath, `data with type "${nameOfIfClause()}" didn't conform to definition of "${nameOfIfClause()}"`, data);
		case "pattern": return errorString(dataPath, `  didn't match pattern "${params.pattern}"`, data);
		case "type": return errorString(dataPath, `  isn't "${params.type}"`, data);
		case "anyOf": return errorString(dataPath, `didn't match any possibility`, data);
		case "oneOf": return errorString(dataPath, `didn't match any possibility`, data);
		default: return errorString(dataPath, message, data, ellipses(JSON.stringify(params)));
	}
};
const processErrors = (errors) => {
	const dataPathStringLengths = R.map((e) => e.schemaPath.length, errors);
	const maxStringLength = Math.max(...dataPathStringLengths);
	return errors.map((e) => ajvErrorAsString(e, maxStringLength));
};

//#endregion
//#region src/ai-json-schemas/ai-json.json
var ai_json_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/ai-json.json",
	definitions: {
		"direction": {
			"type": "string",
			"enum": ["uni", "bi"]
		},
		"poiId": {
			"type": "string",
			"pattern": "ID[0-9]+"
		},
		"xyPoint": {
			"type": "object",
			"properties": {
				"x": { "type": "number" },
				"y": { "type": "number" }
			},
			"additionalItems": false
		},
		"rectangle": {
			"type": "object",
			"properties": {
				"top": { "type": "number" },
				"left": { "type": "number" },
				"bottom": { "type": "number" },
				"right": { "type": "number" }
			},
			"required": [
				"top",
				"left",
				"bottom",
				"right"
			],
			"additionalItems": false
		},
		"firstSecond": {
			"type": "object",
			"properties": {
				"first": { "type": "number" },
				"second": { "type": "number" }
			},
			"required": ["first", "second"],
			"additionalItems": false
		},
		"aiJsonNavpathPoint": {
			"type": "object",
			"properties": {
				"corner": { "type": "boolean" },
				"in": { "$ref": "#/definitions/xyPoint" },
				"out": { "$ref": "#/definitions/xyPoint" },
				"p": { "$ref": "#/definitions/xyPoint" }
			}
		},
		"aiJsonPortalNavpath": {
			"type": "object",
			"properties": {
				"delay": { "type": "number" },
				"poi": { "type": "string" },
				"points": {
					"type": "array",
					"items": { "$ref": "#/definitions/xyPoint" }
				},
				"secure": { "type": "boolean" }
			},
			"required": [
				"delay",
				"poi",
				"points",
				"secure"
			],
			"additionalProperties": false
		},
		"aiJsonPortal": {
			"type": "object",
			"properties": {
				"delay": { "type": "number" },
				"direction": { "$ref": "#/definitions/direction" },
				"dstPoId": { "$ref": "#/definitions/poiId" },
				"srcPoId": { "$ref": "#/definitions/poiId" },
				"navPathName": { "type": "string" },
				"navPath": { "$ref": "#/definitions/aiJsonPortalNavpath" }
			},
			"required": [
				"delay",
				"direction",
				"dstPoId",
				"srcPoId",
				"navPath",
				"navPathName"
			],
			"additionalProperties": false
		},
		"aiJsonPortals": {
			"type": "object",
			"properties": {
				"panelUrl": {
					"type": "string",
					"format": "uri"
				},
				"version": { "type": "number" },
				"venuecode": { "$ref": "string-patterns.json#/definitions/venueId" },
				"portals": {
					"type": "array",
					"items": { "$ref": "#/definitions/aiJsonPortal" }
				}
			},
			"required": [
				"panelUrl",
				"portals",
				"version",
				"venuecode"
			],
			"additionalProperties": false
		},
		"ailayer": {
			"type": "object",
			"title": "AI Layer",
			"properties": {
				"name": { "type": "string" },
				"features": { "$ref": "#/definitions/features" },
				"isLocked": { "type": "boolean" },
				"isVisible": { "type": "boolean" },
				"isExpanded": { "type": "boolean" }
			},
			"required": [],
			"additionalProperties": false
		},
		"aiLayers": {
			"type": "array",
			"title": "AI Layers",
			"items": { "$ref": "#/definitions/ailayer" }
		},
		"aiJsonProperties": {
			"type": "object",
			"properties": {
				"artboards": { "type": "array" },
				"colorModel": { "type": "number" },
				"width": { "type": "number" },
				"height": { "type": "number" },
				"maxArtboardBounds": { "$ref": "#/definitions/rectangle" },
				"origin": { "$ref": "#/definitions/firstSecond" },
				"ruler": { "$ref": "#/definitions/firstSecond" }
			},
			"required": [
				"artboards",
				"colorModel",
				"width",
				"height",
				"maxArtboardBounds",
				"origin",
				"ruler"
			],
			"additionalItems": false
		},
		"badgeFeature": {
			"type": "object",
			"properties": {
				"centerPoint": { "$ref": "#/definitions/xyPoint" },
				"rotation": { "type": "number" },
				"scaling": { "type": "number" },
				"subid": { "$ref": "string-patterns.json#/definitions/featureId" }
			},
			"required": ["centerPoint", "subid"],
			"additionalItems": false
		},
		"geojsonGeometry": {
			"type": "object",
			"properties": {
				"type": {
					"type": "string",
					"enum": [
						"LineString",
						"Point",
						"Polygon"
					]
				},
				"coordinates": { "type": "array" }
			},
			"required": ["type", "coordinates"],
			"additionalItems": false
		},
		"drawingProperties": {
			"type": "object",
			"properties": {
				"type": {
					"type": "string",
					"enum": [
						"Polygon",
						"line",
						"Text"
					]
				},
				"fillColor": { "type": "string" },
				"strokeColor": { "type": "string" },
				"strokeAlign": { "$ref": "string-patterns.json#/definitions/strokeAlign" }
			},
			"required": ["type"],
			"additionalItems": false
		},
		"geometryFeature": {
			"type": "object",
			"properties": {
				"geometry": { "$ref": "#/definitions/geojsonGeometry" },
				"properties": { "$ref": "#/definitions/drawingProperties" },
				"subid": { "$ref": "string-patterns.json#/definitions/featureId" }
			},
			"required": ["geometry", "properties"],
			"additionalItems": false
		},
		"aiEntityPoi": {
			"type": "object",
			"properties": {
				"name": { "type": "string" },
				"type": {
					"type": "string",
					"enum": [
						"polygon",
						"line",
						"line_group",
						"point",
						"touch"
					]
				},
				"category": { "type": "string" },
				"id": { "type": "number" },
				"kind": {
					"type": "string",
					"enum": ["Element", "POI"]
				},
				"location": { "$ref": "string-patterns.json#/definitions/uuid" }
			},
			"required": [
				"name",
				"type",
				"category",
				"id",
				"kind",
				"location"
			],
			"additionalProperties": false
		},
		"singleElementPoi": {
			"type": "object",
			"properties": {
				"geometry": { "$ref": "#/definitions/geojsonGeometry" },
				"poi": { "$ref": "#/definitions/aiEntityPoi" },
				"properties": { "$ref": "#/definitions/drawingProperties" }
			},
			"required": [
				"geometry",
				"poi",
				"properties"
			],
			"additionalItems": false
		},
		"aiEntity": {
			"type": "object",
			"properties": {
				"features": { "$ref": "#/definitions/features" },
				"poi": { "$ref": "#/definitions/aiEntityPoi" },
				"subid": { "$ref": "string-patterns.json#/definitions/featureId" },
				"name": { "type": "string" },
				"rotation": { "type": "number" },
				"scaling": { "type": "number" },
				"isGroup": { "type": "boolean" },
				"isLocked": { "type": "boolean" },
				"isVisible": { "type": "boolean" },
				"isExpanded": { "type": "boolean" }
			},
			"required": ["features", "poi"],
			"additionalProperties": false
		},
		"recursiveFeature": {
			"type": "object",
			"properties": {
				"features": { "$ref": "#/definitions/features" },
				"name": { "type": "string" },
				"rotation": { "type": "number" },
				"scaling": { "type": "number" },
				"subid": { "$ref": "string-patterns.json#/definitions/featureId" },
				"isGroup": { "type": "boolean" },
				"isLocked": { "type": "boolean" },
				"isVisible": { "type": "boolean" },
				"isExpanded": { "type": "boolean" }
			},
			"required": ["features"],
			"additionalProperties": false
		},
		"isLackingSpecialField": { "not": { "anyOf": [
			{ "required": ["centerPoint"] },
			{ "required": ["poi"] },
			{ "required": ["geometry"] }
		] } },
		"isPoiNotGeometry": { "allOf": [{ "required": ["poi"] }, { "not": { "required": ["geometry"] } }] },
		"isGeometryNotPoi": { "allOf": [{ "required": ["geometry"] }, { "not": { "required": ["poi"] } }] },
		"feature": { "allOf": [
			{
				"if": { "required": ["poi", "geometry"] },
				"then": { "$ref": "#/definitions/singleElementPoi" }
			},
			{
				"if": { "required": ["centerPoint"] },
				"then": { "$ref": "#/definitions/badgeFeature" }
			},
			{
				"if": { "$ref": "#/definitions/isGeometryNotPoi" },
				"then": { "$ref": "#/definitions/geometryFeature" }
			},
			{
				"if": { "$ref": "#/definitions/isPoiNotGeometry" },
				"then": { "$ref": "#/definitions/aiEntity" }
			},
			{
				"if": { "$ref": "#/definitions/isLackingSpecialField" },
				"then": { "$ref": "#/definitions/recursiveFeature" }
			}
		] },
		"features": {
			"type": "array",
			"items": { "$ref": "#/definitions/feature" }
		}
	},
	type: "object",
	title: "AI JSON",
	properties: {
		"properties": { "$ref": "#/definitions/aiJsonProperties" },
		"ailayers": { "$ref": "#/definitions/aiLayers" },
		"portals": { "$ref": "#/definitions/aiJsonPortals" },
		"features": { "$ref": "#/definitions/features" }
	},
	required: [
		"features",
		"properties",
		"ailayers",
		"portals"
	],
	additionalProperties: true
};

//#endregion
//#region src/ai-json-schemas/ai-json-venue.json
var ai_json_venue_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/venue.json",
	type: "object",
	title: "Venue",
	definitions: { "venueId": { "oneOf": [{
		"type": "string",
		"pattern": "^[a-z0-9:]+$"
	}, {
		"type": "string",
		"pattern": "^VENUEID$"
	}] } },
	properties: {
		"assetFormatVersion": { "type": "string" },
		"boundingBox": {
			"type": "object",
			"title": "Bounding Box",
			"properties": {
				"n": {
					"type": "number",
					"minimum": -90,
					"maximum": 90
				},
				"e": {
					"type": "number",
					"minimum": -180,
					"maximum": 180
				},
				"s": {
					"type": "number",
					"minimum": -90,
					"maximum": 90
				},
				"w": {
					"type": "number",
					"minimum": -180,
					"maximum": 180
				}
			},
			"required": [
				"n",
				"e",
				"s",
				"w"
			],
			"constructorParameterOrder": [
				"n",
				"e",
				"s",
				"w"
			],
			"additionalProperties": false
		},
		"fixedGeometry": {
			"type": "object",
			"title": "Fixed Geometry",
			"properties": {
				"utmData": {
					"type": "object",
					"title": "Utm Data",
					"definitions": { "utmZone": {
						"type": "string",
						"pattern": "[0-9]{2}s?"
					} },
					"properties": {
						"utmZone": {
							"type": "string",
							"pattern": "[0-9]{2}s?"
						},
						"rect": {
							"type": "object",
							"title": "Rectangle",
							"properties": {
								"x": { "type": "number" },
								"y": { "type": "number" },
								"width": {
									"type": "number",
									"min": 0
								},
								"height": {
									"type": "number",
									"min": 0
								}
							},
							"required": [
								"x",
								"y",
								"width",
								"height"
							],
							"constructorParameterOrder": [
								"x",
								"y",
								"width",
								"height"
							],
							"additionalProperties": false
						}
					},
					"required": ["utmZone", "rect"],
					"constructorParameterOrder": ["utmZone", "rect"],
					"additionalProperties": false
				},
				"viewBox": {
					"type": "object",
					"title": "Rectangle",
					"properties": {
						"x": { "type": "number" },
						"y": { "type": "number" },
						"width": {
							"type": "number",
							"min": 0
						},
						"height": {
							"type": "number",
							"min": 0
						}
					},
					"required": [
						"x",
						"y",
						"width",
						"height"
					],
					"constructorParameterOrder": [
						"x",
						"y",
						"width",
						"height"
					],
					"additionalProperties": false
				}
			},
			"required": ["viewBox", "utmData"],
			"constructorParameterOrder": ["viewBox", "utmData"],
			"additionalProperties": false
		},
		"id": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"name": { "type": "string" },
		"venueCategory": { "type": "string" },
		"venueCenter": {
			"title": "LatLng Array",
			"type": "array",
			"items": {
				"type": "number",
				"minimum": -180,
				"maximum": 180
			},
			"minItems": 2,
			"maxItems": 2
		}
	},
	required: [
		"assetFormatVersion",
		"boundingBox",
		"fixedGeometry",
		"id",
		"name",
		"venueCategory",
		"venueCenter"
	],
	constructorParameterOrder: [
		"assetFormatVersion",
		"boundingBox",
		"fixedGeometry",
		"id",
		"name",
		"venueCategory",
		"venueCenter"
	],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/ai-layer.json
var ai_layer_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/ai-layer.json",
	title: "AI Layer",
	objectType: "AiLayer",
	type: "object",
	definitions: { "layerType": {
		"type": "string",
		"enum": [
			"Bounds",
			"Entity",
			"Feature",
			"Geofence",
			"Label",
			"NavigationLine",
			"Section",
			"Other"
		]
	} },
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"layerType": {
			"type": "string",
			"enum": [
				"Bounds",
				"Entity",
				"Feature",
				"Geofence",
				"Label",
				"NavigationLine",
				"Section",
				"Other"
			]
		},
		"parentId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"orderingWithinParent": {
			"type": "string",
			"pattern": "^.[0-9]+$"
		},
		"boundsId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"entityId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"featureId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"geofenceId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"labelId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"navigationLineId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"sectionId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueAddressUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"structureUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"insideLevelUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"outsideLevelUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"insideWalkwayUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"outsideWalkwayUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"isExpanded": { "type": "boolean" },
		"isGroup": { "type": "boolean" },
		"isLocked": { "type": "boolean" },
		"isVisible": { "type": "boolean" },
		"name": {
			"type": "string",
			"$comment": "Name of the layer as it appears in the AI layers panel"
		},
		"aiLayer": { "type": "string" }
	},
	required: [
		"id",
		"venueId",
		"layerType"
	],
	constructorParameterOrder: [
		"id",
		"venueId",
		"layerType",
		"parentId",
		"orderingWithinParent",
		"boundsId",
		"entityId",
		"featureId",
		"geofenceId",
		"labelId",
		"navigationLineId",
		"sectionId",
		"venueUuid",
		"venueAddressUuid",
		"structureUuid",
		"insideLevelUuid",
		"outsideLevelUuid",
		"insideWalkwayUuid",
		"outsideWalkwayUuid",
		"isExpanded",
		"isGroup",
		"isLocked",
		"isVisible",
		"name",
		"aiLayer"
	],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/ai-subfeature.json
var ai_subfeature_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/ai-text-sub-feature.json",
	type: "object",
	title: "AI Subfeature",
	definitions: {
		"xyCoordinateArray": {
			"type": "array",
			"items": { "type": "number" },
			"minItems": 2,
			"maxItems": 2
		},
		"pointGeometry": {
			"type": "object",
			"properties": {
				"type": { "constant": "Point" },
				"coordinates": {
					"type": "array",
					"items": { "type": "number" },
					"minItems": 2,
					"maxItems": 2
				}
			}
		},
		"textProperties": {
			"type": "object",
			"properties": {
				"type": { "constant": "Text" },
				"align": {
					"type": "string",
					"enum": [
						"left",
						"center",
						"right"
					]
				},
				"fillColor": {
					"type": "string",
					"pattern": "^#[0-9a-fA-F]{6}$"
				},
				"fontName": { "type": "string" },
				"fontSize": { "type": "number" },
				"text": { "type": "string" }
			}
		}
	},
	properties: {
		"geometry": {
			"type": "object",
			"properties": {
				"type": { "constant": "Point" },
				"coordinates": {
					"type": "array",
					"items": { "type": "number" },
					"minItems": 2,
					"maxItems": 2
				}
			}
		},
		"properties": {
			"type": "object",
			"properties": {
				"type": { "constant": "Text" },
				"align": {
					"type": "string",
					"enum": [
						"left",
						"center",
						"right"
					]
				},
				"fillColor": {
					"type": "string",
					"pattern": "^#[0-9a-fA-F]{6}$"
				},
				"fontName": { "type": "string" },
				"fontSize": { "type": "number" },
				"text": { "type": "string" }
			}
		},
		"subid": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		}
	},
	required: [
		"geometry",
		"properties",
		"subid"
	],
	constructorParameterOrder: [
		"geometry",
		"properties",
		"subid"
	],
	additionalProperties: true
};

//#endregion
//#region src/ai-json-schemas/assorted-extra-object.json
var assorted_extra_object_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/assorted-extra-object.json",
	title: "Assorted Extra Object",
	type: "object",
	objectType: "AssortedExtraObject",
	definitions: { "state-machine-state": {
		"type": "string",
		"enum": ["open", "permanently-closed"],
		"default": "open",
		"$comment": "might require further discussion. if its really only going to be 1 of these two options, might just be better as a boolean"
	} },
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"idIndexWithinVenue": {
			"type": "number",
			"minimum": 1
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"name": {
			"type": "string",
			"minLength": 1
		},
		"category": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"state": {
			"type": "string",
			"enum": ["open", "permanently-closed"],
			"default": "open",
			"$comment": "might require further discussion. if its really only going to be 1 of these two options, might just be better as a boolean"
		}
	},
	required: [
		"id",
		"idIndexWithinVenue",
		"venueId",
		"name",
		"category",
		"state"
	],
	constructorParameterOrder: [
		"id",
		"idIndexWithinVenue",
		"venueId",
		"name",
		"category",
		"state"
	]
};

//#endregion
//#region src/ai-json-schemas/bounding-box.json
var bounding_box_default$3 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/bounding-box.json",
	type: "object",
	title: "Bounding Box",
	properties: {
		"n": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"e": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"s": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"w": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		}
	},
	required: [
		"n",
		"e",
		"s",
		"w"
	],
	constructorParameterOrder: [
		"n",
		"e",
		"s",
		"w"
	],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/fixed-geometry.json
var fixed_geometry_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/fixed-geometry.json",
	type: "object",
	title: "Fixed Geometry",
	properties: {
		"utmData": {
			"type": "object",
			"title": "Utm Data",
			"definitions": { "utmZone": {
				"type": "string",
				"pattern": "[0-9]{2}s?"
			} },
			"properties": {
				"utmZone": {
					"type": "string",
					"pattern": "[0-9]{2}s?"
				},
				"rect": {
					"type": "object",
					"title": "Rectangle",
					"properties": {
						"x": { "type": "number" },
						"y": { "type": "number" },
						"width": {
							"type": "number",
							"min": 0
						},
						"height": {
							"type": "number",
							"min": 0
						}
					},
					"required": [
						"x",
						"y",
						"width",
						"height"
					],
					"constructorParameterOrder": [
						"x",
						"y",
						"width",
						"height"
					],
					"additionalProperties": false
				}
			},
			"required": ["utmZone", "rect"],
			"constructorParameterOrder": ["utmZone", "rect"],
			"additionalProperties": false
		},
		"viewBox": {
			"type": "object",
			"title": "Rectangle",
			"properties": {
				"x": { "type": "number" },
				"y": { "type": "number" },
				"width": {
					"type": "number",
					"min": 0
				},
				"height": {
					"type": "number",
					"min": 0
				}
			},
			"required": [
				"x",
				"y",
				"width",
				"height"
			],
			"constructorParameterOrder": [
				"x",
				"y",
				"width",
				"height"
			],
			"additionalProperties": false
		}
	},
	required: ["viewBox", "utmData"],
	constructorParameterOrder: ["viewBox", "utmData"],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/geometry.json
var geometry_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/ai/geometry.json",
	title: "AI coordinate-system Geometry",
	definitions: {
		"geometryType": {
			"type": "string",
			"enum": [
				"Point",
				"LineString",
				"Polygon"
			]
		},
		"aiPoint": {
			"type": "array",
			"items": { "type": "number" },
			"minItems": 2,
			"maxItems": 6
		},
		"aiLine": {
			"type": "array",
			"items": {
				"type": "array",
				"items": { "type": "number" },
				"minItems": 2,
				"maxItems": 6
			},
			"minItems": 2
		},
		"aiPolygon": {
			"type": "array",
			"items": {
				"type": "array",
				"items": {
					"type": "array",
					"items": { "type": "number" },
					"minItems": 2,
					"maxItems": 6
				},
				"minItems": 2
			},
			"minItems": 1
		},
		"aiCoordinates": { "oneOf": [
			{
				"type": "array",
				"items": { "type": "number" },
				"minItems": 2,
				"maxItems": 6
			},
			{
				"type": "array",
				"items": {
					"type": "array",
					"items": { "type": "number" },
					"minItems": 2,
					"maxItems": 6
				},
				"minItems": 2
			},
			{
				"type": "array",
				"items": {
					"type": "array",
					"items": {
						"type": "array",
						"items": { "type": "number" },
						"minItems": 2,
						"maxItems": 6
					},
					"minItems": 2
				},
				"minItems": 1
			}
		] },
		"aiGeometry": {
			"type": "object",
			"properties": {
				"type": {
					"type": "string",
					"enum": [
						"Point",
						"LineString",
						"Polygon"
					]
				},
				"coordinates": { "oneOf": [
					{
						"type": "array",
						"items": { "type": "number" },
						"minItems": 2,
						"maxItems": 6
					},
					{
						"type": "array",
						"items": {
							"type": "array",
							"items": { "type": "number" },
							"minItems": 2,
							"maxItems": 6
						},
						"minItems": 2
					},
					{
						"type": "array",
						"items": {
							"type": "array",
							"items": {
								"type": "array",
								"items": { "type": "number" },
								"minItems": 2,
								"maxItems": 6
							},
							"minItems": 2
						},
						"minItems": 1
					}
				] }
			}
		}
	}
};

//#endregion
//#region src/ai-json-schemas/latlng-array.json
var latlng_array_default$3 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/latlng-array.json",
	title: "LatLng Array",
	type: "array",
	items: {
		"type": "number",
		"minimum": -180,
		"maximum": 180
	},
	minItems: 2,
	maxItems: 2
};

//#endregion
//#region src/ai-json-schemas/navigation-line.json
var navigation_line_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/ai/navigation-line.json",
	type: "object",
	objectType: "NavigationLine",
	definitions: {
		"navigationLineType": {
			"type": "string",
			"enum": ["highway", "driveway"]
		},
		"navInfo": {
			"type": "object",
			"properties": {
				"corridor": { "type": "boolean" },
				"delay": {
					"type": "number",
					"minimum": 0
				},
				"poi": {
					"type": "string",
					"minLength": 0
				},
				"secure": { "type": "boolean" }
			},
			"required": [
				"corridor",
				"delay",
				"secure"
			],
			"additionalProperties": false
		}
	},
	title: "Navigation Line",
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"geometry": {
			"title": "AI coordinate-system Geometry",
			"definitions": {
				"geometryType": {
					"type": "string",
					"enum": [
						"Point",
						"LineString",
						"Polygon"
					]
				},
				"aiPoint": {
					"type": "array",
					"items": { "type": "number" },
					"minItems": 2,
					"maxItems": 6
				},
				"aiLine": {
					"type": "array",
					"items": {
						"type": "array",
						"items": { "type": "number" },
						"minItems": 2,
						"maxItems": 6
					},
					"minItems": 2
				},
				"aiPolygon": {
					"type": "array",
					"items": {
						"type": "array",
						"items": {
							"type": "array",
							"items": { "type": "number" },
							"minItems": 2,
							"maxItems": 6
						},
						"minItems": 2
					},
					"minItems": 1
				},
				"aiCoordinates": { "oneOf": [
					{
						"type": "array",
						"items": { "type": "number" },
						"minItems": 2,
						"maxItems": 6
					},
					{
						"type": "array",
						"items": {
							"type": "array",
							"items": { "type": "number" },
							"minItems": 2,
							"maxItems": 6
						},
						"minItems": 2
					},
					{
						"type": "array",
						"items": {
							"type": "array",
							"items": {
								"type": "array",
								"items": { "type": "number" },
								"minItems": 2,
								"maxItems": 6
							},
							"minItems": 2
						},
						"minItems": 1
					}
				] },
				"aiGeometry": {
					"type": "object",
					"properties": {
						"type": {
							"type": "string",
							"enum": [
								"Point",
								"LineString",
								"Polygon"
							]
						},
						"coordinates": { "oneOf": [
							{
								"type": "array",
								"items": { "type": "number" },
								"minItems": 2,
								"maxItems": 6
							},
							{
								"type": "array",
								"items": {
									"type": "array",
									"items": { "type": "number" },
									"minItems": 2,
									"maxItems": 6
								},
								"minItems": 2
							},
							{
								"type": "array",
								"items": {
									"type": "array",
									"items": {
										"type": "array",
										"items": { "type": "number" },
										"minItems": 2,
										"maxItems": 6
									},
									"minItems": 2
								},
								"minItems": 1
							}
						] }
					}
				}
			}
		},
		"type": {
			"type": "string",
			"enum": ["highway", "driveway"]
		},
		"navInfo": {
			"type": "object",
			"properties": {
				"corridor": { "type": "boolean" },
				"delay": {
					"type": "number",
					"minimum": 0
				},
				"poi": {
					"type": "string",
					"minLength": 0
				},
				"secure": { "type": "boolean" }
			},
			"required": [
				"corridor",
				"delay",
				"secure"
			],
			"additionalProperties": false
		},
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"strokeColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$"
		},
		"strokeWidth": {
			"type": "number",
			"minimum": 0
		},
		"strokeAlign": {
			"type": "string",
			"enum": [
				"inside",
				"center",
				"outside"
			]
		}
	},
	required: [
		"id",
		"venueId",
		"geometry",
		"type"
	],
	constructorParameterOrder: [
		"id",
		"venueId",
		"geometry",
		"type",
		"navInfo",
		"levelId",
		"strokeColor",
		"strokeWidth",
		"strokeAlign"
	],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/rectangle.json
var rectangle_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/rectangle.json",
	type: "object",
	title: "Rectangle",
	properties: {
		"x": { "type": "number" },
		"y": { "type": "number" },
		"width": {
			"type": "number",
			"min": 0
		},
		"height": {
			"type": "number",
			"min": 0
		}
	},
	required: [
		"x",
		"y",
		"width",
		"height"
	],
	constructorParameterOrder: [
		"x",
		"y",
		"width",
		"height"
	],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/string-patterns.json
var string_patterns_default$5 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/string-patterns.json",
	title: "String Patterns",
	definitions: {
		"aiJsonId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"align": {
			"type": "string",
			"enum": [
				"left",
				"center",
				"right"
			]
		},
		"categoryName": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"color": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$"
		},
		"dateTime": {
			"type": "string",
			"format": "date-time"
		},
		"entityId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"featureId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"numberString": {
			"type": "string",
			"pattern": "^[-+]?(?:\\d*\\.\\d+|\\d+\\.?)$"
		},
		"ordinalId": {
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"strokeAlign": {
			"type": "string",
			"enum": [
				"inside",
				"center",
				"outside"
			]
		},
		"structureId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
		}] },
		"subid": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"utmZone": {
			"type": "string",
			"pattern": "[0-9]{2}s?"
		},
		"uuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"version": {
			"type": "string",
			"pattern": "20[0-9]{2}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}$"
		},
		"vmsUuid": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9]+_[0-9]+$"
		}
	}
};

//#endregion
//#region src/ai-json-schemas/top-level-ai-layer.json
var top_level_ai_layer_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/top-level-ai-layer.json",
	title: "Top Level Ai Layer",
	objectType: "TopLevelAiLayer",
	type: "object",
	definitions: { "layerType": {
		"type": "string",
		"enum": [
			"Bounds",
			"Entity",
			"Feature",
			"Geofence",
			"Label",
			"NavigationLine",
			"Section",
			"Other"
		]
	} },
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"layerType": {
			"type": "string",
			"enum": [
				"Bounds",
				"Entity",
				"Feature",
				"Geofence",
				"Label",
				"NavigationLine",
				"Section",
				"Other"
			]
		},
		"aiLayer": { "type": "string" },
		"artboards": { "type": "array" },
		"colorModel": { "type": "number" },
		"height": { "type": "number" },
		"maxArtboardBounds": { "type": "object" },
		"name": {
			"type": "string",
			"$comment": "Name of the layer as it appears in the AI layers panel"
		},
		"origin": { "type": "object" },
		"panelUrl": { "type": "string" },
		"portals": { "type": "array" },
		"ruler": { "type": "object" },
		"showGrid": { "type": "boolean" },
		"snapGrid": { "type": "boolean" },
		"venuecode": { "type": "string" },
		"version": { "type": "number" },
		"width": { "type": "number" }
	},
	required: [
		"id",
		"venueId",
		"layerType"
	],
	constructorParameterOrder: [
		"id",
		"venueId",
		"layerType",
		"aiLayer",
		"artboards",
		"colorModel",
		"height",
		"maxArtboardBounds",
		"name",
		"origin",
		"panelUrl",
		"portals",
		"ruler",
		"showGrid",
		"snapGrid",
		"venuecode",
		"version",
		"width"
	],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/utm-data.json
var utm_data_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-ai-json.com/schemas/utm-data.json",
	type: "object",
	title: "Utm Data",
	definitions: { "utmZone": {
		"type": "string",
		"pattern": "[0-9]{2}s?"
	} },
	properties: {
		"utmZone": {
			"type": "string",
			"pattern": "[0-9]{2}s?"
		},
		"rect": {
			"type": "object",
			"title": "Rectangle",
			"properties": {
				"x": { "type": "number" },
				"y": { "type": "number" },
				"width": {
					"type": "number",
					"min": 0
				},
				"height": {
					"type": "number",
					"min": 0
				}
			},
			"required": [
				"x",
				"y",
				"width",
				"height"
			],
			"constructorParameterOrder": [
				"x",
				"y",
				"width",
				"height"
			],
			"additionalProperties": false
		}
	},
	required: ["utmZone", "rect"],
	constructorParameterOrder: ["utmZone", "rect"],
	additionalProperties: false
};

//#endregion
//#region src/ai-json-schemas/index.js
var ai_json_schemas_exports = /* @__PURE__ */ __exportAll({
	AiJson: () => ai_json_default,
	AiJsonVenue: () => ai_json_venue_default,
	AiLayer: () => ai_layer_default,
	AiSubfeature: () => ai_subfeature_default,
	AssortedExtraObject: () => assorted_extra_object_default,
	BoundingBox: () => bounding_box_default$3,
	FixedGeometry: () => fixed_geometry_default$2,
	Geometry: () => geometry_default,
	LatLngArray: () => latlng_array_default$3,
	NavigationLine: () => navigation_line_default,
	Rectangle: () => rectangle_default$2,
	StringPatterns: () => string_patterns_default$5,
	TopLevelAiLayer: () => top_level_ai_layer_default,
	UtmData: () => utm_data_default$2
});

//#endregion
//#region src/api-marketplace/dynamic-pois/dynamic-poi.json
var dynamic_poi_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/dynamic-poi.json",
	title: "Dynamic Poi",
	description: "A dynamic POI, that can have various dynamic components that represent different kinds of dynamic functionality for the POI.",
	properties: { "dynamicData": {
		"type": "object",
		"properties": {
			"openClosed": {
				"description": "The open-closed status of the POI. Note: this might be redudant with other components",
				"title": "Open Closed POIs",
				"type": "object",
				"properties": {
					"expiration": {
						"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
						"type": "integer",
						"minimum": 0
					},
					"isOpen": {
						"type": ["boolean", "null"],
						"description": "True if the POI represented by this data is currently open. If the property is not availible then the status is unknown."
					},
					"poiId": {
						"type": "string",
						"description": "The poiId used to reference the POI that the dynamic data is for."
					}
				},
				"required": ["expiration", "isOpen"],
				"additionalProperties": false
			},
			"queue": {
				"description": "The queue wait time for the POI",
				"title": "Dynamic Queue Data",
				"type": "object",
				"properties": {
					"expiration": {
						"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
						"type": "integer",
						"minimum": 0
					},
					"poiId": {
						"type": "string",
						"description": "The entity id of the entity associated with the queue. Assuming there is one. TODO rename this entity?"
					},
					"clientDynamicEntityId": {
						"type": "string",
						"description": "The id Api Marketplace uses to associate the dynamic queue data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
					},
					"isTemporarilyClosed": {
						"type": "boolean",
						"description": "True if the poi represented by this data is temporarily closed."
					},
					"isQueueTimeDefault": {
						"type": "boolean",
						"description": "Is the queueTime live data or is it a default value."
					},
					"queueTime": {
						"type": "number",
						"minimum": 0,
						"maximum": 1440,
						"description": "The queue wait. Time in minutes. 0 min < x < 1 day (1440 min)"
					}
				},
				"required": [
					"expiration",
					"isQueueTimeDefault",
					"isTemporarilyClosed",
					"queueTime",
					"clientDynamicEntityId"
				],
				"additionalProperties": false
			},
			"parking": {
				"description": "The parking info for the POI",
				"title": "Parking POI",
				"type": "object",
				"properties": {
					"poiId": {
						"type": "string",
						"description": "The entity id of the entity associated with the parking lot. Assuming there is one."
					},
					"clientDynamicEntityId": {
						"type": "string",
						"description": "The id Api Marketplace uses to associate the parking lot data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
					},
					"expiration": {
						"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
						"type": "integer",
						"minimum": 0
					},
					"rateDay": {
						"type": "string",
						"description": "A statement about the price per day(e.g. $8 per day). Includes localized currency symbol."
					},
					"rateDayAmount": {
						"type": "number",
						"minimum": 0,
						"description": "The numeric cost of parking per day relative to the local currency. Does not include localization information."
					},
					"rateHour": {
						"type": "string",
						"description": "A statement about the price per hour(e.g. $2 per hour). Includes localized currency symbol."
					},
					"rateHourAmount": {
						"type": "number",
						"minimum": 0,
						"description": "The numeric cost of parking per hour relative to the local currency. Does not include localization information."
					},
					"lotStatus": {
						"type": "string",
						"enum": [
							"Open",
							"Closed",
							"Full",
							"Nearing Capacity"
						],
						"description": "Denotes the overall status of parking lot"
					}
				},
				"required": ["expiration", "clientDynamicEntityId"],
				"additionalProperties": false
			}
		},
		"additionalProperties": false
	} },
	required: ["dynamicData"],
	type: "object"
};

//#endregion
//#region src/api-marketplace/dynamic-pois/open-closed-data.json
var open_closed_data_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/open-closed-data.json",
	title: "Open Closed POIs",
	description: "Defines schema for POI with dynamic open/closed status.",
	type: "object",
	properties: {
		"expiration": {
			"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
			"type": "integer",
			"minimum": 0
		},
		"isOpen": {
			"type": ["boolean", "null"],
			"description": "True if the POI represented by this data is currently open. If the property is not availible then the status is unknown."
		},
		"poiId": {
			"type": "string",
			"description": "The poiId used to reference the POI that the dynamic data is for."
		}
	},
	required: ["expiration", "isOpen"],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/dynamic-pois/open-closed-data-array.json
var open_closed_data_array_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/open-closed-data-array.json",
	title: "Open Closed POIs Array",
	description: "Defines schema for POIs with dynamic open/closed statuses.",
	type: "array",
	items: {
		"type": "object",
		"properties": {
			"expiration": {
				"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
				"type": "integer",
				"minimum": 0
			},
			"isOpen": {
				"type": ["boolean", "null"],
				"description": "True if the POI represented by this data is currently open. If the property is not availible then the status is unknown."
			},
			"poiId": {
				"type": "string",
				"description": "The poiId used to reference the POI that the dynamic data is for."
			},
			"clientDynamicEntityId": {
				"type": "string",
				"description": "The id Api Marketplace uses to associate the dynamic open data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
			}
		},
		"required": [
			"expiration",
			"isOpen",
			"clientDynamicEntityId"
		],
		"additionalProperties": false
	}
};

//#endregion
//#region src/api-marketplace/dynamic-pois/queue-data.json
var queue_data_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/queue-data.json",
	title: "Dynamic Queue Data",
	description: "Defines dynamic queue data.",
	type: "object",
	properties: {
		"expiration": {
			"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
			"type": "integer",
			"minimum": 0
		},
		"poiId": {
			"type": "string",
			"description": "The entity id of the entity associated with the queue. Assuming there is one. TODO rename this entity?"
		},
		"clientDynamicEntityId": {
			"type": "string",
			"description": "The id Api Marketplace uses to associate the dynamic queue data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
		},
		"isTemporarilyClosed": {
			"type": "boolean",
			"description": "True if the poi represented by this data is temporarily closed."
		},
		"isQueueTimeDefault": {
			"type": "boolean",
			"description": "Is the queueTime live data or is it a default value."
		},
		"queueTime": {
			"type": "number",
			"minimum": 0,
			"maximum": 1440,
			"description": "The queue wait. Time in minutes. 0 min < x < 1 day (1440 min)"
		}
	},
	required: [
		"expiration",
		"isQueueTimeDefault",
		"isTemporarilyClosed",
		"queueTime",
		"clientDynamicEntityId"
	],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/dynamic-pois/queue-data-array.json
var queue_data_array_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/queue-data-array.json",
	title: "Dynamic Queue Data Array",
	description: "Defines dynamic queue data array.",
	type: "array",
	items: {
		"title": "Dynamic Queue Data",
		"description": "Defines dynamic queue data.",
		"type": "object",
		"properties": {
			"expiration": {
				"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
				"type": "integer",
				"minimum": 0
			},
			"poiId": {
				"type": "string",
				"description": "The entity id of the entity associated with the queue. Assuming there is one. TODO rename this entity?"
			},
			"clientDynamicEntityId": {
				"type": "string",
				"description": "The id Api Marketplace uses to associate the dynamic queue data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
			},
			"isTemporarilyClosed": {
				"type": "boolean",
				"description": "True if the poi represented by this data is temporarily closed."
			},
			"isQueueTimeDefault": {
				"type": "boolean",
				"description": "Is the queueTime live data or is it a default value."
			},
			"queueTime": {
				"type": "number",
				"minimum": 0,
				"maximum": 1440,
				"description": "The queue wait. Time in minutes. 0 min < x < 1 day (1440 min)"
			}
		},
		"required": [
			"expiration",
			"isQueueTimeDefault",
			"isTemporarilyClosed",
			"queueTime",
			"clientDynamicEntityId"
		],
		"additionalProperties": false
	}
};

//#endregion
//#region src/api-marketplace/dynamic-pois/parking-data.json
var parking_data_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/parking-data.json",
	title: "Parking POI",
	description: "Defines the dynamic information for a parking structure.",
	type: "object",
	properties: {
		"poiId": {
			"type": "string",
			"description": "The entity id of the entity associated with the parking lot. Assuming there is one."
		},
		"clientDynamicEntityId": {
			"type": "string",
			"description": "The id Api Marketplace uses to associate the parking lot data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
		},
		"expiration": {
			"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
			"type": "integer",
			"minimum": 0
		},
		"rateDay": {
			"type": "string",
			"description": "A statement about the price per day(e.g. $8 per day). Includes localized currency symbol."
		},
		"rateDayAmount": {
			"type": "number",
			"minimum": 0,
			"description": "The numeric cost of parking per day relative to the local currency. Does not include localization information."
		},
		"rateHour": {
			"type": "string",
			"description": "A statement about the price per hour(e.g. $2 per hour). Includes localized currency symbol."
		},
		"rateHourAmount": {
			"type": "number",
			"minimum": 0,
			"description": "The numeric cost of parking per hour relative to the local currency. Does not include localization information."
		},
		"lotStatus": {
			"type": "string",
			"enum": [
				"Open",
				"Closed",
				"Full",
				"Nearing Capacity"
			],
			"description": "Denotes the overall status of parking lot"
		}
	},
	required: ["expiration", "clientDynamicEntityId"],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/dynamic-pois/parking-data-array.json
var parking_data_array_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/parking-data-array.json",
	title: "Parking POI Array",
	description: "Defines the dynamic information for multiple parking structures.",
	type: "array",
	items: {
		"title": "Parking POI",
		"description": "Defines the dynamic information for a parking structure.",
		"type": "object",
		"properties": {
			"poiId": {
				"type": "string",
				"description": "The entity id of the entity associated with the parking lot. Assuming there is one."
			},
			"clientDynamicEntityId": {
				"type": "string",
				"description": "The id Api Marketplace uses to associate the parking lot data with the published poi by searching published assets for the clientDynamicEntityId in the entities dynamic integration object"
			},
			"expiration": {
				"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
				"type": "integer",
				"minimum": 0
			},
			"rateDay": {
				"type": "string",
				"description": "A statement about the price per day(e.g. $8 per day). Includes localized currency symbol."
			},
			"rateDayAmount": {
				"type": "number",
				"minimum": 0,
				"description": "The numeric cost of parking per day relative to the local currency. Does not include localization information."
			},
			"rateHour": {
				"type": "string",
				"description": "A statement about the price per hour(e.g. $2 per hour). Includes localized currency symbol."
			},
			"rateHourAmount": {
				"type": "number",
				"minimum": 0,
				"description": "The numeric cost of parking per hour relative to the local currency. Does not include localization information."
			},
			"lotStatus": {
				"type": "string",
				"enum": [
					"Open",
					"Closed",
					"Full",
					"Nearing Capacity"
				],
				"description": "Denotes the overall status of parking lot"
			}
		},
		"required": ["expiration", "clientDynamicEntityId"],
		"additionalProperties": false
	}
};

//#endregion
//#region src/api-marketplace/dynamic-pois/dynamic-routing-data.json
var dynamic_routing_data_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/dynamic-routing-data.json",
	title: "Dynamic Routing POI",
	description: "Defines properties of a data type used for dynamic routing.",
	type: "object",
	properties: {
		"expiration": {
			"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
			"type": "integer",
			"minimum": 0
		},
		"navGraphHidden": {
			"type": "boolean",
			"description": "True if the POI represented by this data is currently to be removed from the NavGraph in wayfinder. False if the POI should be used."
		},
		"navGraphHiddenReason": {
			"type": "string",
			"description": "A descriptive string to be used in the webengine UI that explains why this POI is currently not able to be routed from to or through."
		},
		"poiId": {
			"type": "string",
			"description": "The poiId used to reference the POI that this dynamic data is for."
		}
	},
	required: [
		"expiration",
		"navGraphHidden",
		"poiId"
	],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/dynamic-pois/dynamic-routing-data-array.json
var dynamic_routing_data_array_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/api-marketplace/dynamic-pois/dynamic-routing-data-array.json",
	title: "Open Closed POIs Array",
	description: "Defines schema for POIs with dynamic routing related properties.",
	type: "array",
	items: {
		"type": "object",
		"properties": {
			"expiration": {
				"description": "The time at which the data is considered expired in milliseconds, relative to midnight on January 1, 1970, UTC.",
				"type": "integer",
				"minimum": 0
			},
			"navGraphHidden": {
				"type": "boolean",
				"description": "True if the POI represented by this data is currently to be removed from the NavGraph in wayfinder. False if the POI should be used."
			},
			"navGraphHiddenReason": {
				"type": "string",
				"description": "A descriptive string to be used in the webengine UI that explains why this POI is currently not able to be routed from to or through."
			},
			"poiId": {
				"type": "string",
				"description": "The poiId used to reference the POI that this dynamic data is for."
			}
		},
		"required": [
			"expiration",
			"navGraphHidden",
			"poiId"
		],
		"additionalProperties": false
	}
};

//#endregion
//#region src/api-marketplace/flight-status.json
var flight_status_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-api-marketplace.com/schemas/flight-status.json",
	type: "object",
	title: "Flight Status",
	description: "Defines the schema of a flight status used to show dynamic flight information on a map.",
	definitions: { "dateObject": {
		"type": "object",
		"properties": {
			"dateLocal": {
				"oneOf": [{
					"type": "string",
					"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
					"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
				}, {
					"type": "string",
					"maxLength": 0
				}],
				"description": "Relative time to the time of the airport that corresponds to the date."
			},
			"dateUtc": {
				"oneOf": [{
					"type": "string",
					"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
					"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
				}, {
					"type": "string",
					"maxLength": 0
				}],
				"description": "Coordinated Universal Time (UTC). Same across timezones."
			}
		},
		"required": ["dateUtc"],
		"additionalProperties": false
	} },
	properties: {
		"arrivalAirportFsCode": {
			"description": "The code of the arrival airport. Either IATA or ICOA code will work.  Used to search appendix.",
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"arrivalDate": {
			"description": "The arrival date that was scheduled. Local time should be relative to the arrival airport",
			"type": "object",
			"properties": {
				"dateLocal": {
					"oneOf": [{
						"type": "string",
						"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
						"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
					}, {
						"type": "string",
						"maxLength": 0
					}],
					"description": "Relative time to the time of the airport that corresponds to the date."
				},
				"dateUtc": {
					"oneOf": [{
						"type": "string",
						"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
						"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
					}, {
						"type": "string",
						"maxLength": 0
					}],
					"description": "Coordinated Universal Time (UTC). Same across timezones."
				}
			},
			"required": ["dateUtc"],
			"additionalProperties": false
		},
		"airportResources": {
			"type": "object",
			"properties": {
				"arrivalBagClaim": {
					"type": "string",
					"description": "Baggage claim location."
				},
				"arrivalGate": {
					"type": "string",
					"description": "Gate of arrival."
				},
				"arrivalTerminal": {
					"type": "string",
					"description": "Terminal of arrival."
				},
				"departureGate": {
					"type": "string",
					"description": "Gate of departure."
				},
				"departureTerminal": {
					"type": "string",
					"description": "Terminal of departure."
				}
			},
			"additionalProperties": false
		},
		"carrierFsCode": {
			"description": "Code for the operating carrier. Either Iata or Icoa code. Used to search appendix.",
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"departureAirportFsCode": {
			"description": "The code of the departure airport. Either IATA or ICOA code will work.  Used to search appendix.",
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"departureDate": {
			"description": "The departure date that was scheduled. Local time should be relative to the departure airport",
			"type": "object",
			"properties": {
				"dateLocal": {
					"oneOf": [{
						"type": "string",
						"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
						"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
					}, {
						"type": "string",
						"maxLength": 0
					}],
					"description": "Relative time to the time of the airport that corresponds to the date."
				},
				"dateUtc": {
					"oneOf": [{
						"type": "string",
						"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
						"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
					}, {
						"type": "string",
						"maxLength": 0
					}],
					"description": "Coordinated Universal Time (UTC). Same across timezones."
				}
			},
			"required": ["dateUtc"],
			"additionalProperties": false
		},
		"flightId": {
			"description": "Includes the flight number and other info. Sometimes this is derived from other properties.",
			"type": "string",
			"maxLength": 128,
			"$comment": "The unique identifier for the flight"
		},
		"flightNumber": {
			"type": "string",
			"pattern": "[0-9]{1,5}",
			"$comment": "The codeshare flight identification number and any additional characters. https://en.wikipedia.org/wiki/Flight_number"
		},
		"flightStatusUpdates": {
			"type": "array",
			"items": {
				"type": "object",
				"properties": { "updatedAt": {
					"type": "object",
					"properties": {
						"dateLocal": {
							"oneOf": [{
								"type": "string",
								"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
								"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
							}, {
								"type": "string",
								"maxLength": 0
							}],
							"description": "Relative time to the time of the airport that corresponds to the date."
						},
						"dateUtc": {
							"oneOf": [{
								"type": "string",
								"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
								"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
							}, {
								"type": "string",
								"maxLength": 0
							}],
							"description": "Coordinated Universal Time (UTC). Same across timezones."
						}
					},
					"required": ["dateUtc"],
					"additionalProperties": false
				} },
				"required": ["updatedAt"],
				"additionalProperties": false
			},
			"minItems": 1,
			"uniqueItems": true,
			"description": "Updates that to the flight that have occured since it was published."
		},
		"operationalTimes": {
			"type": "object",
			"properties": {
				"estimatedGateArrival": {
					"type": "object",
					"properties": {
						"dateLocal": {
							"oneOf": [{
								"type": "string",
								"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
								"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
							}, {
								"type": "string",
								"maxLength": 0
							}],
							"description": "Relative time to the time of the airport that corresponds to the date."
						},
						"dateUtc": {
							"oneOf": [{
								"type": "string",
								"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
								"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
							}, {
								"type": "string",
								"maxLength": 0
							}],
							"description": "Coordinated Universal Time (UTC). Same across timezones."
						}
					},
					"required": ["dateUtc"],
					"additionalProperties": false,
					"description": "The expected time of actual arrival."
				},
				"estimatedGateDeparture": {
					"type": "object",
					"properties": {
						"dateLocal": {
							"oneOf": [{
								"type": "string",
								"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
								"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
							}, {
								"type": "string",
								"maxLength": 0
							}],
							"description": "Relative time to the time of the airport that corresponds to the date."
						},
						"dateUtc": {
							"oneOf": [{
								"type": "string",
								"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
								"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
							}, {
								"type": "string",
								"maxLength": 0
							}],
							"description": "Coordinated Universal Time (UTC). Same across timezones."
						}
					},
					"required": ["dateUtc"],
					"additionalProperties": false,
					"description": "The expected time of actual departure."
				}
			},
			"required": ["estimatedGateArrival", "estimatedGateDeparture"],
			"additionalProperties": false
		},
		"status": {
			"description": "A code denoting the status of flight. A(Active) | C(Canceled) | L(Landed) | S(Scheduled) | U(Unknown).",
			"type": "string",
			"pattern": "^A|C|L|S|U$",
			"$comment": "Restrict to MOL? enums: https://developer.flightstats.com/api-docs/flightstatus/v2/flightstatusresponse"
		}
	},
	required: [
		"arrivalAirportFsCode",
		"arrivalDate",
		"airportResources",
		"carrierFsCode",
		"departureAirportFsCode",
		"departureDate",
		"flightId",
		"flightNumber",
		"flightStatusUpdates",
		"operationalTimes",
		"status"
	],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/flight-listing.json
var flight_listing_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-api-marketplace.com/schemas/flight-listing.json",
	type: "object",
	title: "Flight Status",
	description: "Defines the schema of a flight status used to show dynamic flight information on a map.",
	definitions: { "flightLegs": {
		"type": "object",
		"properties": {
			"appendix": {
				"type": "object",
				"properties": {
					"airlines": {
						"type": "array",
						"items": {
							"type": "object",
							"title": "Airline",
							"description": "Schema for a particular airline company. Used in flight status.",
							"properties": {
								"iata": {
									"type": "string",
									"pattern": "[A-Z0-9a-z]{2,5}"
								},
								"icao": {
									"type": "string",
									"pattern": "[A-Z0-9a-z]{2,5}"
								},
								"name": {
									"type": "string",
									"maxLength": 255
								}
							},
							"required": [
								"iata",
								"icao",
								"name"
							],
							"additionalProperties": false
						}
					},
					"airports": {
						"type": "array",
						"items": {
							"type": "object",
							"title": "Airport",
							"description": "Schema for a particular airport company. Used in flight status.",
							"properties": {
								"iata": {
									"type": "string",
									"pattern": "[A-Z0-9a-z]{2,5}"
								},
								"city": { "type": "string" },
								"utcOffsetHours": {
									"type": "integer",
									"maximum": 14,
									"minimum": -12
								},
								"timeZone": {
									"type": "string",
									"maxLength": 32,
									"$comment": "e.g. America/Los_Angeles"
								}
							},
							"required": [
								"iata",
								"city",
								"utcOffsetHours",
								"timeZone"
							],
							"additionalProperties": false
						}
					}
				}
			},
			"flightStatuses": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Flight Status",
					"description": "Defines the schema of a flight status used to show dynamic flight information on a map.",
					"definitions": { "dateObject": {
						"type": "object",
						"properties": {
							"dateLocal": {
								"oneOf": [{
									"type": "string",
									"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
									"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
								}, {
									"type": "string",
									"maxLength": 0
								}],
								"description": "Relative time to the time of the airport that corresponds to the date."
							},
							"dateUtc": {
								"oneOf": [{
									"type": "string",
									"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
									"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
								}, {
									"type": "string",
									"maxLength": 0
								}],
								"description": "Coordinated Universal Time (UTC). Same across timezones."
							}
						},
						"required": ["dateUtc"],
						"additionalProperties": false
					} },
					"properties": {
						"arrivalAirportFsCode": {
							"description": "The code of the arrival airport. Either IATA or ICOA code will work.  Used to search appendix.",
							"type": "string",
							"pattern": "[A-Z0-9a-z]{2,5}"
						},
						"arrivalDate": {
							"description": "The arrival date that was scheduled. Local time should be relative to the arrival airport",
							"type": "object",
							"properties": {
								"dateLocal": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
										"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Relative time to the time of the airport that corresponds to the date."
								},
								"dateUtc": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
										"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Coordinated Universal Time (UTC). Same across timezones."
								}
							},
							"required": ["dateUtc"],
							"additionalProperties": false
						},
						"airportResources": {
							"type": "object",
							"properties": {
								"arrivalBagClaim": {
									"type": "string",
									"description": "Baggage claim location."
								},
								"arrivalGate": {
									"type": "string",
									"description": "Gate of arrival."
								},
								"arrivalTerminal": {
									"type": "string",
									"description": "Terminal of arrival."
								},
								"departureGate": {
									"type": "string",
									"description": "Gate of departure."
								},
								"departureTerminal": {
									"type": "string",
									"description": "Terminal of departure."
								}
							},
							"additionalProperties": false
						},
						"carrierFsCode": {
							"description": "Code for the operating carrier. Either Iata or Icoa code. Used to search appendix.",
							"type": "string",
							"pattern": "[A-Z0-9a-z]{2,5}"
						},
						"departureAirportFsCode": {
							"description": "The code of the departure airport. Either IATA or ICOA code will work.  Used to search appendix.",
							"type": "string",
							"pattern": "[A-Z0-9a-z]{2,5}"
						},
						"departureDate": {
							"description": "The departure date that was scheduled. Local time should be relative to the departure airport",
							"type": "object",
							"properties": {
								"dateLocal": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
										"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Relative time to the time of the airport that corresponds to the date."
								},
								"dateUtc": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
										"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Coordinated Universal Time (UTC). Same across timezones."
								}
							},
							"required": ["dateUtc"],
							"additionalProperties": false
						},
						"flightId": {
							"description": "Includes the flight number and other info. Sometimes this is derived from other properties.",
							"type": "string",
							"maxLength": 128,
							"$comment": "The unique identifier for the flight"
						},
						"flightNumber": {
							"type": "string",
							"pattern": "[0-9]{1,5}",
							"$comment": "The codeshare flight identification number and any additional characters. https://en.wikipedia.org/wiki/Flight_number"
						},
						"flightStatusUpdates": {
							"type": "array",
							"items": {
								"type": "object",
								"properties": { "updatedAt": {
									"type": "object",
									"properties": {
										"dateLocal": {
											"oneOf": [{
												"type": "string",
												"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
												"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
											}, {
												"type": "string",
												"maxLength": 0
											}],
											"description": "Relative time to the time of the airport that corresponds to the date."
										},
										"dateUtc": {
											"oneOf": [{
												"type": "string",
												"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
												"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
											}, {
												"type": "string",
												"maxLength": 0
											}],
											"description": "Coordinated Universal Time (UTC). Same across timezones."
										}
									},
									"required": ["dateUtc"],
									"additionalProperties": false
								} },
								"required": ["updatedAt"],
								"additionalProperties": false
							},
							"minItems": 1,
							"uniqueItems": true,
							"description": "Updates that to the flight that have occured since it was published."
						},
						"operationalTimes": {
							"type": "object",
							"properties": {
								"estimatedGateArrival": {
									"type": "object",
									"properties": {
										"dateLocal": {
											"oneOf": [{
												"type": "string",
												"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
												"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
											}, {
												"type": "string",
												"maxLength": 0
											}],
											"description": "Relative time to the time of the airport that corresponds to the date."
										},
										"dateUtc": {
											"oneOf": [{
												"type": "string",
												"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
												"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
											}, {
												"type": "string",
												"maxLength": 0
											}],
											"description": "Coordinated Universal Time (UTC). Same across timezones."
										}
									},
									"required": ["dateUtc"],
									"additionalProperties": false,
									"description": "The expected time of actual arrival."
								},
								"estimatedGateDeparture": {
									"type": "object",
									"properties": {
										"dateLocal": {
											"oneOf": [{
												"type": "string",
												"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
												"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
											}, {
												"type": "string",
												"maxLength": 0
											}],
											"description": "Relative time to the time of the airport that corresponds to the date."
										},
										"dateUtc": {
											"oneOf": [{
												"type": "string",
												"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
												"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
											}, {
												"type": "string",
												"maxLength": 0
											}],
											"description": "Coordinated Universal Time (UTC). Same across timezones."
										}
									},
									"required": ["dateUtc"],
									"additionalProperties": false,
									"description": "The expected time of actual departure."
								}
							},
							"required": ["estimatedGateArrival", "estimatedGateDeparture"],
							"additionalProperties": false
						},
						"status": {
							"description": "A code denoting the status of flight. A(Active) | C(Canceled) | L(Landed) | S(Scheduled) | U(Unknown).",
							"type": "string",
							"pattern": "^A|C|L|S|U$",
							"$comment": "Restrict to MOL? enums: https://developer.flightstats.com/api-docs/flightstatus/v2/flightstatusresponse"
						}
					},
					"required": [
						"arrivalAirportFsCode",
						"arrivalDate",
						"airportResources",
						"carrierFsCode",
						"departureAirportFsCode",
						"departureDate",
						"flightId",
						"flightNumber",
						"flightStatusUpdates",
						"operationalTimes",
						"status"
					],
					"additionalProperties": false
				}
			}
		}
	} },
	properties: {
		"arrivals": {
			"type": "object",
			"properties": {
				"appendix": {
					"type": "object",
					"properties": {
						"airlines": {
							"type": "array",
							"items": {
								"type": "object",
								"title": "Airline",
								"description": "Schema for a particular airline company. Used in flight status.",
								"properties": {
									"iata": {
										"type": "string",
										"pattern": "[A-Z0-9a-z]{2,5}"
									},
									"icao": {
										"type": "string",
										"pattern": "[A-Z0-9a-z]{2,5}"
									},
									"name": {
										"type": "string",
										"maxLength": 255
									}
								},
								"required": [
									"iata",
									"icao",
									"name"
								],
								"additionalProperties": false
							}
						},
						"airports": {
							"type": "array",
							"items": {
								"type": "object",
								"title": "Airport",
								"description": "Schema for a particular airport company. Used in flight status.",
								"properties": {
									"iata": {
										"type": "string",
										"pattern": "[A-Z0-9a-z]{2,5}"
									},
									"city": { "type": "string" },
									"utcOffsetHours": {
										"type": "integer",
										"maximum": 14,
										"minimum": -12
									},
									"timeZone": {
										"type": "string",
										"maxLength": 32,
										"$comment": "e.g. America/Los_Angeles"
									}
								},
								"required": [
									"iata",
									"city",
									"utcOffsetHours",
									"timeZone"
								],
								"additionalProperties": false
							}
						}
					}
				},
				"flightStatuses": {
					"type": "array",
					"items": {
						"type": "object",
						"title": "Flight Status",
						"description": "Defines the schema of a flight status used to show dynamic flight information on a map.",
						"definitions": { "dateObject": {
							"type": "object",
							"properties": {
								"dateLocal": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
										"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Relative time to the time of the airport that corresponds to the date."
								},
								"dateUtc": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
										"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Coordinated Universal Time (UTC). Same across timezones."
								}
							},
							"required": ["dateUtc"],
							"additionalProperties": false
						} },
						"properties": {
							"arrivalAirportFsCode": {
								"description": "The code of the arrival airport. Either IATA or ICOA code will work.  Used to search appendix.",
								"type": "string",
								"pattern": "[A-Z0-9a-z]{2,5}"
							},
							"arrivalDate": {
								"description": "The arrival date that was scheduled. Local time should be relative to the arrival airport",
								"type": "object",
								"properties": {
									"dateLocal": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
											"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Relative time to the time of the airport that corresponds to the date."
									},
									"dateUtc": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
											"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Coordinated Universal Time (UTC). Same across timezones."
									}
								},
								"required": ["dateUtc"],
								"additionalProperties": false
							},
							"airportResources": {
								"type": "object",
								"properties": {
									"arrivalBagClaim": {
										"type": "string",
										"description": "Baggage claim location."
									},
									"arrivalGate": {
										"type": "string",
										"description": "Gate of arrival."
									},
									"arrivalTerminal": {
										"type": "string",
										"description": "Terminal of arrival."
									},
									"departureGate": {
										"type": "string",
										"description": "Gate of departure."
									},
									"departureTerminal": {
										"type": "string",
										"description": "Terminal of departure."
									}
								},
								"additionalProperties": false
							},
							"carrierFsCode": {
								"description": "Code for the operating carrier. Either Iata or Icoa code. Used to search appendix.",
								"type": "string",
								"pattern": "[A-Z0-9a-z]{2,5}"
							},
							"departureAirportFsCode": {
								"description": "The code of the departure airport. Either IATA or ICOA code will work.  Used to search appendix.",
								"type": "string",
								"pattern": "[A-Z0-9a-z]{2,5}"
							},
							"departureDate": {
								"description": "The departure date that was scheduled. Local time should be relative to the departure airport",
								"type": "object",
								"properties": {
									"dateLocal": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
											"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Relative time to the time of the airport that corresponds to the date."
									},
									"dateUtc": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
											"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Coordinated Universal Time (UTC). Same across timezones."
									}
								},
								"required": ["dateUtc"],
								"additionalProperties": false
							},
							"flightId": {
								"description": "Includes the flight number and other info. Sometimes this is derived from other properties.",
								"type": "string",
								"maxLength": 128,
								"$comment": "The unique identifier for the flight"
							},
							"flightNumber": {
								"type": "string",
								"pattern": "[0-9]{1,5}",
								"$comment": "The codeshare flight identification number and any additional characters. https://en.wikipedia.org/wiki/Flight_number"
							},
							"flightStatusUpdates": {
								"type": "array",
								"items": {
									"type": "object",
									"properties": { "updatedAt": {
										"type": "object",
										"properties": {
											"dateLocal": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
													"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Relative time to the time of the airport that corresponds to the date."
											},
											"dateUtc": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
													"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Coordinated Universal Time (UTC). Same across timezones."
											}
										},
										"required": ["dateUtc"],
										"additionalProperties": false
									} },
									"required": ["updatedAt"],
									"additionalProperties": false
								},
								"minItems": 1,
								"uniqueItems": true,
								"description": "Updates that to the flight that have occured since it was published."
							},
							"operationalTimes": {
								"type": "object",
								"properties": {
									"estimatedGateArrival": {
										"type": "object",
										"properties": {
											"dateLocal": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
													"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Relative time to the time of the airport that corresponds to the date."
											},
											"dateUtc": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
													"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Coordinated Universal Time (UTC). Same across timezones."
											}
										},
										"required": ["dateUtc"],
										"additionalProperties": false,
										"description": "The expected time of actual arrival."
									},
									"estimatedGateDeparture": {
										"type": "object",
										"properties": {
											"dateLocal": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
													"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Relative time to the time of the airport that corresponds to the date."
											},
											"dateUtc": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
													"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Coordinated Universal Time (UTC). Same across timezones."
											}
										},
										"required": ["dateUtc"],
										"additionalProperties": false,
										"description": "The expected time of actual departure."
									}
								},
								"required": ["estimatedGateArrival", "estimatedGateDeparture"],
								"additionalProperties": false
							},
							"status": {
								"description": "A code denoting the status of flight. A(Active) | C(Canceled) | L(Landed) | S(Scheduled) | U(Unknown).",
								"type": "string",
								"pattern": "^A|C|L|S|U$",
								"$comment": "Restrict to MOL? enums: https://developer.flightstats.com/api-docs/flightstatus/v2/flightstatusresponse"
							}
						},
						"required": [
							"arrivalAirportFsCode",
							"arrivalDate",
							"airportResources",
							"carrierFsCode",
							"departureAirportFsCode",
							"departureDate",
							"flightId",
							"flightNumber",
							"flightStatusUpdates",
							"operationalTimes",
							"status"
						],
						"additionalProperties": false
					}
				}
			}
		},
		"departures": {
			"type": "object",
			"properties": {
				"appendix": {
					"type": "object",
					"properties": {
						"airlines": {
							"type": "array",
							"items": {
								"type": "object",
								"title": "Airline",
								"description": "Schema for a particular airline company. Used in flight status.",
								"properties": {
									"iata": {
										"type": "string",
										"pattern": "[A-Z0-9a-z]{2,5}"
									},
									"icao": {
										"type": "string",
										"pattern": "[A-Z0-9a-z]{2,5}"
									},
									"name": {
										"type": "string",
										"maxLength": 255
									}
								},
								"required": [
									"iata",
									"icao",
									"name"
								],
								"additionalProperties": false
							}
						},
						"airports": {
							"type": "array",
							"items": {
								"type": "object",
								"title": "Airport",
								"description": "Schema for a particular airport company. Used in flight status.",
								"properties": {
									"iata": {
										"type": "string",
										"pattern": "[A-Z0-9a-z]{2,5}"
									},
									"city": { "type": "string" },
									"utcOffsetHours": {
										"type": "integer",
										"maximum": 14,
										"minimum": -12
									},
									"timeZone": {
										"type": "string",
										"maxLength": 32,
										"$comment": "e.g. America/Los_Angeles"
									}
								},
								"required": [
									"iata",
									"city",
									"utcOffsetHours",
									"timeZone"
								],
								"additionalProperties": false
							}
						}
					}
				},
				"flightStatuses": {
					"type": "array",
					"items": {
						"type": "object",
						"title": "Flight Status",
						"description": "Defines the schema of a flight status used to show dynamic flight information on a map.",
						"definitions": { "dateObject": {
							"type": "object",
							"properties": {
								"dateLocal": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
										"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Relative time to the time of the airport that corresponds to the date."
								},
								"dateUtc": {
									"oneOf": [{
										"type": "string",
										"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
										"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
									}, {
										"type": "string",
										"maxLength": 0
									}],
									"description": "Coordinated Universal Time (UTC). Same across timezones."
								}
							},
							"required": ["dateUtc"],
							"additionalProperties": false
						} },
						"properties": {
							"arrivalAirportFsCode": {
								"description": "The code of the arrival airport. Either IATA or ICOA code will work.  Used to search appendix.",
								"type": "string",
								"pattern": "[A-Z0-9a-z]{2,5}"
							},
							"arrivalDate": {
								"description": "The arrival date that was scheduled. Local time should be relative to the arrival airport",
								"type": "object",
								"properties": {
									"dateLocal": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
											"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Relative time to the time of the airport that corresponds to the date."
									},
									"dateUtc": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
											"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Coordinated Universal Time (UTC). Same across timezones."
									}
								},
								"required": ["dateUtc"],
								"additionalProperties": false
							},
							"airportResources": {
								"type": "object",
								"properties": {
									"arrivalBagClaim": {
										"type": "string",
										"description": "Baggage claim location."
									},
									"arrivalGate": {
										"type": "string",
										"description": "Gate of arrival."
									},
									"arrivalTerminal": {
										"type": "string",
										"description": "Terminal of arrival."
									},
									"departureGate": {
										"type": "string",
										"description": "Gate of departure."
									},
									"departureTerminal": {
										"type": "string",
										"description": "Terminal of departure."
									}
								},
								"additionalProperties": false
							},
							"carrierFsCode": {
								"description": "Code for the operating carrier. Either Iata or Icoa code. Used to search appendix.",
								"type": "string",
								"pattern": "[A-Z0-9a-z]{2,5}"
							},
							"departureAirportFsCode": {
								"description": "The code of the departure airport. Either IATA or ICOA code will work.  Used to search appendix.",
								"type": "string",
								"pattern": "[A-Z0-9a-z]{2,5}"
							},
							"departureDate": {
								"description": "The departure date that was scheduled. Local time should be relative to the departure airport",
								"type": "object",
								"properties": {
									"dateLocal": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
											"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Relative time to the time of the airport that corresponds to the date."
									},
									"dateUtc": {
										"oneOf": [{
											"type": "string",
											"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
											"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
										}, {
											"type": "string",
											"maxLength": 0
										}],
										"description": "Coordinated Universal Time (UTC). Same across timezones."
									}
								},
								"required": ["dateUtc"],
								"additionalProperties": false
							},
							"flightId": {
								"description": "Includes the flight number and other info. Sometimes this is derived from other properties.",
								"type": "string",
								"maxLength": 128,
								"$comment": "The unique identifier for the flight"
							},
							"flightNumber": {
								"type": "string",
								"pattern": "[0-9]{1,5}",
								"$comment": "The codeshare flight identification number and any additional characters. https://en.wikipedia.org/wiki/Flight_number"
							},
							"flightStatusUpdates": {
								"type": "array",
								"items": {
									"type": "object",
									"properties": { "updatedAt": {
										"type": "object",
										"properties": {
											"dateLocal": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
													"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Relative time to the time of the airport that corresponds to the date."
											},
											"dateUtc": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
													"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Coordinated Universal Time (UTC). Same across timezones."
											}
										},
										"required": ["dateUtc"],
										"additionalProperties": false
									} },
									"required": ["updatedAt"],
									"additionalProperties": false
								},
								"minItems": 1,
								"uniqueItems": true,
								"description": "Updates that to the flight that have occured since it was published."
							},
							"operationalTimes": {
								"type": "object",
								"properties": {
									"estimatedGateArrival": {
										"type": "object",
										"properties": {
											"dateLocal": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
													"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Relative time to the time of the airport that corresponds to the date."
											},
											"dateUtc": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
													"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Coordinated Universal Time (UTC). Same across timezones."
											}
										},
										"required": ["dateUtc"],
										"additionalProperties": false,
										"description": "The expected time of actual arrival."
									},
									"estimatedGateDeparture": {
										"type": "object",
										"properties": {
											"dateLocal": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
													"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Relative time to the time of the airport that corresponds to the date."
											},
											"dateUtc": {
												"oneOf": [{
													"type": "string",
													"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
													"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
												}, {
													"type": "string",
													"maxLength": 0
												}],
												"description": "Coordinated Universal Time (UTC). Same across timezones."
											}
										},
										"required": ["dateUtc"],
										"additionalProperties": false,
										"description": "The expected time of actual departure."
									}
								},
								"required": ["estimatedGateArrival", "estimatedGateDeparture"],
								"additionalProperties": false
							},
							"status": {
								"description": "A code denoting the status of flight. A(Active) | C(Canceled) | L(Landed) | S(Scheduled) | U(Unknown).",
								"type": "string",
								"pattern": "^A|C|L|S|U$",
								"$comment": "Restrict to MOL? enums: https://developer.flightstats.com/api-docs/flightstatus/v2/flightstatusresponse"
							}
						},
						"required": [
							"arrivalAirportFsCode",
							"arrivalDate",
							"airportResources",
							"carrierFsCode",
							"departureAirportFsCode",
							"departureDate",
							"flightId",
							"flightNumber",
							"flightStatusUpdates",
							"operationalTimes",
							"status"
						],
						"additionalProperties": false
					}
				}
			}
		}
	},
	required: ["arrivals", "departures"],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/airline.json
var airline_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-api-marketplace.com/schemas/airline.json",
	type: "object",
	title: "Airline",
	description: "Schema for a particular airline company. Used in flight status.",
	properties: {
		"iata": {
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"icao": {
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"name": {
			"type": "string",
			"maxLength": 255
		}
	},
	required: [
		"iata",
		"icao",
		"name"
	],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/airport.json
var airport_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-api-marketplace.com/schemas/airport.json",
	type: "object",
	title: "Airport",
	description: "Schema for a particular airport company. Used in flight status.",
	properties: {
		"iata": {
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"city": { "type": "string" },
		"utcOffsetHours": {
			"type": "integer",
			"maximum": 14,
			"minimum": -12
		},
		"timeZone": {
			"type": "string",
			"maxLength": 32,
			"$comment": "e.g. America/Los_Angeles"
		}
	},
	required: [
		"iata",
		"city",
		"utcOffsetHours",
		"timeZone"
	],
	additionalProperties: false
};

//#endregion
//#region src/api-marketplace/resource-calendar-reservation.json
var resource_calendar_reservation_default = {
	title: "Resource Calendar Reservation",
	definitions: { "scheduleResource": {
		"type": "object",
		"properties": {
			"subject": { "type": "string" },
			"location": { "type": "string" },
			"attendees": {
				"type": "array",
				"items": { "$type": "string" }
			}
		},
		"additionalProperties": false
	} }
};

//#endregion
//#region src/api-marketplace/string-patterns.json
var string_patterns_default$4 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-api-marketplace.com/schemas/string-patterns.json",
	title: "String Patterns",
	definitions: {
		"iataOrIcoaCode": {
			"type": "string",
			"pattern": "[A-Z0-9a-z]{2,5}"
		},
		"dateUtc": {
			"type": "string",
			"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?(?<zulu>Z)?$",
			"$comment": "Not exaclty ISO8601 UTC. Year can be two digits but not here."
		},
		"dateLocal": {
			"type": "string",
			"pattern": "^(?<year>[0-9]{4})-(?<month>1[0-2]|0[1-9])-(?<day>3[01]|0[1-9]|[12][0-9])T(?<hour>2[0-3]|[01][0-9]):(?<min>[0-5][0-9]):(?<sec>[0-5][0-9])(?<millisec>\\.[0-9]+)?$",
			"$comment": "TODO: Currently we allow zulu time for localDate. We should not."
		},
		"flightId": {
			"type": "string",
			"maxLength": 128,
			"$comment": "The unique identifier for the flight"
		},
		"flightNumber": {
			"type": "string",
			"pattern": "[0-9]{1,5}",
			"$comment": "The codeshare flight identification number and any additional characters. https://en.wikipedia.org/wiki/Flight_number"
		},
		"flightStatus": {
			"type": "string",
			"pattern": "^A|C|L|S|U$",
			"$comment": "Restrict to MOL? enums: https://developer.flightstats.com/api-docs/flightstatus/v2/flightstatusresponse"
		},
		"timezone": {
			"type": "string",
			"maxLength": 32,
			"$comment": "e.g. America/Los_Angeles"
		}
	}
};

//#endregion
//#region src/common/numeric-properties.json
var numeric_properties_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://api-marketplace.com/schemas/common/numeric-properties.json",
	title: "Common Properties",
	definitions: { "wholeNumber": {
		"type": "integer",
		"description": "https://www.cuemath.com/numbers/whole-numbers/",
		"minimum": 0
	} }
};

//#endregion
//#region src/api-marketplace/index.js
var api_marketplace_exports = /* @__PURE__ */ __exportAll({
	CommonNumericTypes: () => numeric_properties_default,
	StringPatterns: () => string_patterns_default$4,
	airline: () => airline_default,
	airport: () => airport_default,
	dynamicPoi: () => dynamic_poi_default,
	dynamicRoutingData: () => dynamic_routing_data_default,
	dynamicRoutingDataArray: () => dynamic_routing_data_array_default,
	flightListing: () => flight_listing_default,
	flightStatus: () => flight_status_default,
	openClosedData: () => open_closed_data_default,
	openClosedDataArray: () => open_closed_data_array_default,
	parkingData: () => parking_data_default,
	parkingDataArray: () => parking_data_array_default,
	queueData: () => queue_data_default,
	queueDataArray: () => queue_data_array_default,
	resourceCalendarReservation: () => resource_calendar_reservation_default
});

//#endregion
//#region src/file-schemas/entity.json
var entity_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-file-schemas.com/schemas/entity.json",
	title: "Entity",
	definitions: {
		"additionalAttributes": { "type": "string" },
		"airlineCode": {
			"type": "string",
			"minLength": 1
		},
		"airport": {
			"type": "string",
			"minLength": 1
		},
		"attributes": {
			"type": "string",
			"enum": ["empty", "unknown"]
		},
		"category": { "$ref": "string-patterns.json#/definitions/categoryName" },
		"categorySearch": { "type": "string" },
		"closedEntityId": {
			"type": "string",
			"pattern": "^#[0-9]+$"
		},
		"customSearches": {
			"type": "string",
			"enum": ["rotatefree:true", "rotate180:true"]
		},
		"description": {
			"type": "string",
			"minLength": 1
		},
		"displayProperties": { "type": "string" },
		"displayTags": {
			"type": "array",
			"items": {
				"type": "string",
				"minLength": 1
			}
		},
		"fakeVmsEntityId": {
			"type": "string",
			"pattern": "[a-zA-Z0-9]+_[0-9]+"
		},
		"floor": {
			"type": "string",
			"minLength": 1
		},
		"gate": {
			"type": "string",
			"minLength": 1
		},
		"hours": { "$ref": "string-patterns.json#/definitions/hours" },
		"icon": {
			"type": "string",
			"minLength": 1
		},
		"id": { "$ref": "string-patterns.json#/definitions/entityId" },
		"image": { "$ref": "http://locuslabs-vms.com/schemas/image.json" },
		"images": {
			"type": "array",
			"items": { "$ref": "#/definitions/image" }
		},
		"logo": {
			"type": "string",
			"enum": ["rotatefree:true", "rotate180:true"]
		},
		"name": {
			"type": "string",
			"minLength": 1
		},
		"noDirections": {
			"type": "string",
			"enum": ["none"]
		},
		"phone": {
			"type": "string",
			"minLength": 1
		},
		"rawHours": { "$ref": "string-patterns.json#/definitions/hours" },
		"rotationProperties": {
			"type": "string",
			"enum": ["rotatefree:true", "rotate180:true"]
		},
		"tags": {
			"type": "array",
			"items": {
				"type": "string",
				"minLength": 1
			}
		},
		"terminal": {
			"type": "string",
			"minLength": 1
		},
		"url": { "type": "string" },
		"urlDisplay": {
			"type": "string",
			"enum": ["rotatefree:true", "rotate180:true"]
		},
		"uuid": {
			"type": "string",
			"pattern": "[a-zA-Z0-9]+_[0-9]+"
		},
		"venueId": { "$ref": "string-patterns.json#/definitions/venueId" }
	},
	type: "object",
	properties: {
		"additionalAttributes": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/additionalAttributes" }] },
		"airlineCode": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/airlineCode" }] },
		"airport": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/airport" }] },
		"attributes": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/attributes" }] },
		"category": { "$ref": "#/definitions/category" },
		"categorySearch": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/categorySearch" }] },
		"customSearches": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/customSearches" }] },
		"description": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/description" }] },
		"displayProperties": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/displayProperties" }] },
		"displayTags": { "$ref": "#/definitions/displayTags" },
		"floor": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/floor" }] },
		"gate": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/gate" }] },
		"hours": { "anyOf": [{ "type": "null" }, { "type": "string" }] },
		"icon": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/icon" }] },
		"id": { "anyOf": [{ "$ref": "#/definitions/id" }, { "$ref": "#/definitions/closedEntityId" }] },
		"images": { "$ref": "#/definitions/images" },
		"logo": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/logo" }] },
		"name": { "$ref": "#/definitions/name" },
		"noDirections": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/noDirections" }] },
		"phone": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/phone" }] },
		"rawHours": { "anyOf": [{ "type": "null" }, { "type": "string" }] },
		"rotationProperties": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/rotationProperties" }] },
		"tags": { "$ref": "#/definitions/tags" },
		"terminal": { "$ref": "#/definitions/terminal" },
		"url": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/url" }] },
		"urlDisplay": { "anyOf": [{ "type": "null" }, { "$ref": "#/definitions/urlDisplay" }] },
		"uuid": { "$ref": "#/definitions/fakeVmsEntityId" },
		"venueId": { "$ref": "#/definitions/venueId" }
	},
	required: [
		"airport",
		"category",
		"displayTags",
		"floor",
		"icon",
		"id",
		"images",
		"name",
		"security",
		"tags",
		"terminal",
		"uuid",
		"venueId"
	],
	additionalItems: false
};

//#endregion
//#region src/file-schemas/entity-lookup-table.json
var entity_lookup_table_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-file-schemas.com/schemas/entity-lookup-table.json",
	title: "Entity Lookup Table",
	type: "object",
	patternProperties: { "[0-9]+": { "$ref": "http://locuslabs-file-schemas.com/schemas/entity.json" } },
	additionalItems: false
};

//#endregion
//#region src/file-schemas/image.json
var image_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-file-schemas.com/schemas/image.json",
	title: "Image",
	type: "object",
	properties: {
		"blobId": { "anyOf": [{
			"type": "string",
			"pattern": ".*jpg"
		}, { "$ref": "string-patterns.json#/definitions/uuid" }] },
		"caption": { "type": "string" },
		"venueId": { "$ref": "string-patterns.json#/definitions/venueId" },
		"entityId": { "anyOf": [{ "$ref": "string-patterns.json#/definitions/entityId" }, { "$ref": "string-patterns.json#/definitions/vmsUuid" }] },
		"id": { "anyOf": [{
			"type": "string",
			"enum": ["SHEET"]
		}, { "$ref": "string-patterns.json#/definitions/uuid" }] }
	},
	required: [
		"blobId",
		"caption",
		"venueId",
		"entityId",
		"id"
	],
	additionalItems: false
};

//#endregion
//#region src/file-schemas/string-patterns.json
var string_patterns_default$3 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-file-schemas.com/schemas/string-patterns.json",
	title: "String Patterns",
	definitions: {
		"align": {
			"type": "string",
			"enum": [
				"left",
				"center",
				"right"
			]
		},
		"categoryName": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"color": {
			"type": "string",
			"pattern": "^#[0-9a-f]{6}$"
		},
		"dateTime": {
			"type": "string",
			"format": "date-time"
		},
		"entityId": {
			"type": "string",
			"pattern": "^[0-9]+$"
		},
		"featureId": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"hours": { "anyOf": [
			{ "$ref": "#/definitions/hoursOneDay" },
			{ "$ref": "#/definitions/hoursDayRange" },
			{ "$ref": "#/definitions/hoursDayRangeAndDay" },
			{ "$ref": "#/definitions/hoursDayRangeAndDayRange" },
			{ "$ref": "#/definitions/hoursDayRangeAndDayAndDay" }
		] },
		"hoursOneDay": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRange": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRangeAndDay": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRangeAndDayRange": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRangeAndDayAndDay": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"numberString": {
			"type": "string",
			"pattern": "^[-+]?(?:\\d*\\.\\d+|\\d+\\.?)$"
		},
		"ordinalId": {
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"strokeAlign": {
			"type": "string",
			"enum": [
				"inside",
				"center",
				"outside"
			]
		},
		"structureId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
		}] },
		"subid": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"utmZone": {
			"type": "string",
			"pattern": "[0-9]{2}s?"
		},
		"uuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"version": {
			"type": "string",
			"pattern": "20[0-9]{2}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}$"
		},
		"vmsUuid": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9]+_[0-9]+$"
		}
	}
};

//#endregion
//#region src/file-schemas/venue-data.json
var venue_data_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-file-schemas.com/schemas/venue-data.json",
	type: "object",
	title: "Venue Data",
	patternProperties: { "^[a-z.]+|VENUEID$": { "$ref": "venue-details-schema.json" } },
	additionalProperties: false
};

//#endregion
//#region src/file-schemas/venue-details.json
var venue_details_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-file-schemas.com/schemas/venue-details.json",
	definitions: {
		"airportCode": {
			"type": "string",
			"pattern": "[a-zA-Z0-9]+"
		},
		"formatVersion": {
			"type": "string",
			"pattern": "v[0-9]"
		},
		"levelId": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		},
		"locale": {
			"type": "string",
			"pattern": "[a-z]{2}"
		},
		"structureId": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		},
		"uuid": {
			"type": "string",
			"pattern": "[a-fA-Z0-9]{8}-[a-fA-Z0-9]{4}-[a-fA-Z0-9]{4}-[a-fA-Z0-9]{4}-[a-fA-Z0-9]{12}"
		},
		"venueCategory": {
			"type": "string",
			"enum": [
				"airport",
				"smartcampus",
				"casino"
			]
		},
		"venueId": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9:]+$"
		},
		"lat": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"lng": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"utmZone": { "type": ["number", "string"] },
		"structureIdArray": {
			"type": "array",
			"items": { "$ref": "#/definitions/structureId" },
			"minItems": 1
		},
		"beaconRegion": {
			"title": "Beacon Region",
			"type": "object",
			"properties": {
				"uuid": { "$ref": "#/definitions/uuid" },
				"major": { "type": "number" },
				"minor": { "type": "number" }
			},
			"required": ["uuid"],
			"additionalProperties": false
		},
		"bounds": {
			"title": "Bounds",
			"type": "object",
			"properties": {
				"ne": { "$ref": "#/definitions/latLng" },
				"sw": { "$ref": "#/definitions/latLng" }
			},
			"required": ["ne", "sw"],
			"additionalProperties": false
		},
		"latLng": {
			"title": "LatLng",
			"type": "object",
			"properties": {
				"lat": { "$ref": "#/definitions/lat" },
				"lng": { "$ref": "#/definitions/lng" }
			},
			"required": ["lat", "lng"],
			"additionalProperties": false
		},
		"latLngArray": {
			"type": "array",
			"items": [{ "$ref": "#/definitions/lat" }, { "$ref": "#/definitions/lng" }],
			"additionalItems": false
		},
		"level": {
			"title": "Level",
			"type": "object",
			"properties": {
				"details": { "type": "string" },
				"id": { "$ref": "#/definitions/levelId" },
				"name": { "type": "string" },
				"ordinal": { "type": "number" },
				"clfloor": { "type": "number" }
			},
			"required": [
				"details",
				"id",
				"name",
				"ordinal"
			],
			"additionalProperties": false
		},
		"levels": {
			"title": "Levels",
			"type": "object",
			"patternProperties": { "^[a-zA-Z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$": { "$ref": "#/definitions/level" } },
			"additionalProperties": false
		},
		"structure": {
			"title": "Structure",
			"type": "object",
			"properties": {
				"defaultLevelId": { "$ref": "#/definitions/levelId" },
				"id": { "$ref": "#/definitions/structureId" },
				"levels": { "$ref": "#/definitions/levels" },
				"name": { "type": "string" },
				"shouldDisplay": { "type": "boolean" }
			},
			"required": [
				"defaultLevelId",
				"id",
				"levels",
				"name"
			],
			"additionalProperties": false
		},
		"structures": {
			"title": "Structures",
			"type": "object",
			"patternProperties": { "^[a-zA-Z0-9:]+-[a-zA-Z0-9 : ]+$": { "$ref": "#/definitions/structure" } },
			"additionalProperties": false
		},
		"utmCoordinates": {
			"title": "Utm Coordinates",
			"type": "object",
			"properties": {
				"easting": { "type": "number" },
				"northing": { "type": "number" }
			},
			"required": ["easting", "northing"],
			"additionalProperties": false
		},
		"utmBounds": {
			"title": "Utm Bounds",
			"type": "object",
			"properties": {
				"lowerLeft": { "$ref": "#/definitions/utmCoordinates" },
				"lowerRight": { "$ref": "#/definitions/utmCoordinates" },
				"upperLeft": { "$ref": "#/definitions/utmCoordinates" },
				"upperRight": { "$ref": "#/definitions/utmCoordinates" },
				"utmZone": { "$ref": "#/definitions/utmZone" }
			},
			"required": [
				"lowerLeft",
				"lowerRight",
				"upperLeft",
				"upperRight",
				"utmZone"
			],
			"additionalProperties": false
		},
		"viewBox": {
			"title": "Utm Bounds",
			"type": "object",
			"properties": {
				"x": {
					"type": "number",
					"minimum": 0
				},
				"y": {
					"type": "number",
					"minimum": 0
				},
				"width": {
					"type": "number",
					"minimum": 1
				},
				"height": {
					"type": "number",
					"minimum": 1
				}
			},
			"required": [
				"x",
				"y",
				"width",
				"height"
			],
			"additionalProperties": false
		}
	},
	type: "object",
	title: "Venue Details",
	properties: {
		"address": { "type": "string" },
		"bearing": { "type": "integer" },
		"airportCode": { "$ref": "#/definitions/airportCode" },
		"beaconRegions": {
			"type": "array",
			"items": { "$ref": "#/definitions/beaconRegion" }
		},
		"beaconsUrl": { "type": "string" },
		"bounds": { "$ref": "#/definitions/bounds" },
		"category": { "$ref": "#/definitions/venueCategory" },
		"defaultStructureId": { "$ref": "#/definitions/structureId" },
		"details": { "type": "string" },
		"formatVersion": { "$ref": "#/definitions/formatVersion" },
		"id": { "$ref": "#/definitions/venueId" },
		"locale": { "$ref": "#/definitions/locale" },
		"map": {
			"type": "string",
			"pattern": ".*.svg"
		},
		"mapNav": { "type": "string" },
		"name": { "type": "string" },
		"phone": { "type": "string" },
		"poiDb": { "type": "string" },
		"positioningSupported": {
			"type": "array",
			"items": { "type": "string" }
		},
		"structures": { "$ref": "#/definitions/structures" },
		"structureOrder": { "$ref": "#/definitions/structureIdArray" },
		"selectorOrder": { "$ref": "#/definitions/structureIdArray" },
		"storeInBlobStore": {
			"type": "boolean",
			"default": false
		},
		"utmBounds": { "$ref": "#/definitions/utmBounds" },
		"venueCenter": { "$ref": "#/definitions/latLngArray" },
		"venueOrientation": {
			"type": "string",
			"enum": ["tall", "wide"]
		},
		"venueRadius": { "type": "number" },
		"viewBox": { "$ref": "#/definitions/viewBox" },
		"website": { "type": "string" }
	},
	required: [
		"category",
		"defaultStructureId",
		"details",
		"id",
		"name",
		"phone",
		"structures",
		"venueCenter",
		"venueRadius"
	],
	additionalProperties: false
};

//#endregion
//#region src/file-schemas/index.js
var file_schemas_exports = /* @__PURE__ */ __exportAll({
	Entity: () => entity_default$2,
	EntityLookupTable: () => entity_lookup_table_default,
	Image: () => image_default$1,
	StringPatterns: () => string_patterns_default$3,
	VenueData: () => venue_data_default,
	VenueDetails: () => venue_details_default
});

//#endregion
//#region src/imdf/address.json
var address_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/address.json",
	type: "object",
	title: "Address",
	properties: {
		"street": { "type": "string" },
		"unit": { "type": "string" },
		"locality": { "type": "string" },
		"postalCode": { "type": "string" },
		"postalCodeExt": { "type": "string" },
		"province": {
			"type": "string",
			"pattern": "^[A-Z]{2}-[A-Z0-9]+$"
		},
		"country": {
			"type": "string",
			"pattern": "^[A-Z]{2}$"
		}
	},
	required: [
		"street",
		"locality",
		"province",
		"country"
	],
	constructorParameterOrder: [
		"street",
		"locality",
		"province",
		"country"
	],
	additionalProperties: false
};

//#endregion
//#region src/imdf/bounding-box.json
var bounding_box_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/bounding-box.json",
	type: "object",
	title: "Bounding Box",
	properties: {
		"n": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"e": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"s": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"w": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		}
	},
	required: [
		"n",
		"e",
		"s",
		"w"
	],
	constructorParameterOrder: [
		"n",
		"e",
		"s",
		"w"
	],
	additionalProperties: false
};

//#endregion
//#region src/imdf/contact-information.json
var contact_information_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/contact-information.json",
	type: "object",
	title: "Contact Information",
	properties: {
		"address": { "$ref": "address.json" },
		"hours": { "type": "string" },
		"phone": { "type": "string" },
		"website": { "type": "string" }
	},
	required: [
		"address",
		"hours",
		"phone",
		"website"
	],
	constructorParameterOrder: [
		"address",
		"hours",
		"phone",
		"website"
	],
	additionalProperties: false
};

//#endregion
//#region src/imdf/fixed-geometry.json
var fixed_geometry_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/fixed-geometry.json",
	type: "object",
	title: "Fixed Geometry",
	properties: {
		"utmData": { "$ref": "utm-data.json" },
		"viewBox": { "$ref": "rectangle.json" }
	},
	required: ["viewBox", "utmData"],
	constructorParameterOrder: ["viewBox", "utmData"],
	additionalProperties: false
};

//#endregion
//#region src/imdf/imdf-venue.json
var imdf_venue_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/venue.json",
	type: "object",
	title: "IMDF Venue",
	definitions: { "venueId": { "oneOf": [{
		"type": "string",
		"pattern": "^[a-z0-9:]+$"
	}, {
		"type": "string",
		"pattern": "^VENUEID$"
	}] } },
	properties: {
		"boundingBox": { "$ref": "bounding-box.json" },
		"contactInformation": { "$ref": "contact-information.json" },
		"fixedGeometry": { "$ref": "fixed-geometry.json" },
		"id": { "$ref": "#/definitions/venueId" },
		"name": { "type": "string" },
		"venueCategory": { "type": "string" },
		"venueCenter": { "$ref": "latlng-array.json" }
	},
	required: [
		"boundingBox",
		"contactInformation",
		"fixedGeometry",
		"id",
		"name",
		"venueCategory",
		"venueCenter"
	],
	constructorParameterOrder: [
		"boundingBox",
		"contactInformation",
		"fixedGeometry",
		"id",
		"name",
		"venueCategory",
		"venueCenter"
	],
	additionalProperties: false
};

//#endregion
//#region src/imdf/latlng-array.json
var latlng_array_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/latlng-array.json",
	title: "LatLng Array",
	type: "array",
	items: {
		"type": "number",
		"minimum": -180,
		"maximum": 180
	},
	minItems: 2,
	maxItems: 2
};

//#endregion
//#region src/imdf/rectangle.json
var rectangle_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/rectangle.json",
	type: "object",
	title: "Rectangle",
	properties: {
		"x": { "type": "number" },
		"y": { "type": "number" },
		"width": {
			"type": "number",
			"min": 0
		},
		"height": {
			"type": "number",
			"min": 0
		}
	},
	required: [
		"x",
		"y",
		"width",
		"height"
	],
	constructorParameterOrder: [
		"x",
		"y",
		"width",
		"height"
	],
	additionalProperties: false
};

//#endregion
//#region src/imdf/utm-data.json
var utm_data_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-imdf.com/schemas/utm-data.json",
	type: "object",
	title: "Utm Data",
	definitions: { "utmZone": {
		"type": "string",
		"pattern": "[0-9]{2}s?"
	} },
	properties: {
		"utmZone": { "$ref": "#/definitions/utmZone" },
		"rect": { "$ref": "rectangle.json" }
	},
	required: ["utmZone", "rect"],
	constructorParameterOrder: ["utmZone", "rect"],
	additionalProperties: false
};

//#endregion
//#region src/imdf/index.js
var imdf_exports = /* @__PURE__ */ __exportAll({
	Address: () => address_default$1,
	BoundingBox: () => bounding_box_default$2,
	ContactInformation: () => contact_information_default$1,
	FixedGeometry: () => fixed_geometry_default$1,
	ImdfVenue: () => imdf_venue_default,
	LatLngArray: () => latlng_array_default$2,
	Rectangle: () => rectangle_default$1,
	UtmData: () => utm_data_default$1
});

//#endregion
//#region src/object-model/address.json
var address_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/address.json",
	type: "object",
	title: "Address",
	properties: {
		"street": { "type": "string" },
		"unit": { "type": "string" },
		"locality": { "type": "string" },
		"postalCode": { "type": "string" },
		"postalCodeExt": { "type": "string" },
		"province": {
			"type": "string",
			"pattern": "^[A-Z]{2}-[A-Z0-9]+$"
		},
		"country": {
			"type": "string",
			"pattern": "^[A-Z]{2}$"
		}
	},
	required: [
		"street",
		"locality",
		"province",
		"country"
	],
	constructorParameterOrder: [
		"street",
		"locality",
		"province",
		"country"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/augmented-reality.json
var augmented_reality_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/augmented-reality.json",
	title: "Augmented Reality",
	objectType: "AugmentedReality",
	properties: {
		"isARAvailable": { "type": "boolean" },
		"isARNavAvailable": { "type": "boolean" },
		"isARSignsAvailable": { "type": "boolean" },
		"pathToARFiles": { "type": "string" }
	},
	additionalProperties: false
};

//#endregion
//#region src/object-model/beacon-regions.json
var beacon_regions_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/beacon-regions.json",
	title: "Beacon Regions",
	objectType: "BeaconRegions",
	type: "object",
	properties: {
		"positioningSupported": {
			"type": "array",
			"items": { "type": "string" },
			"$comment": "Can't expose to customers."
		},
		"beaconRegions": {
			"title": "Beacon Regions List",
			"type": "array",
			"items": {
				"title": "Beacon Region",
				"type": "object",
				"properties": {
					"majorId": {
						"type": "integer",
						"minimum": 1,
						"maximum": 65535
					},
					"minorId": {
						"type": "integer",
						"minimum": 1,
						"maximum": 65535
					},
					"uuid": {
						"$comment": "region uuid.",
						"type": "string",
						"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
					}
				},
				"required": ["uuid"],
				"additionalProperties": false
			}
		}
	},
	definitions: {
		"beaconRegionsList": {
			"title": "Beacon Regions List",
			"type": "array",
			"items": {
				"title": "Beacon Region",
				"type": "object",
				"properties": {
					"majorId": {
						"type": "integer",
						"minimum": 1,
						"maximum": 65535
					},
					"minorId": {
						"type": "integer",
						"minimum": 1,
						"maximum": 65535
					},
					"uuid": {
						"$comment": "region uuid.",
						"type": "string",
						"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
					}
				},
				"required": ["uuid"],
				"additionalProperties": false
			}
		},
		"beaconRegion": {
			"title": "Beacon Region",
			"type": "object",
			"properties": {
				"majorId": {
					"type": "integer",
					"minimum": 1,
					"maximum": 65535
				},
				"minorId": {
					"type": "integer",
					"minimum": 1,
					"maximum": 65535
				},
				"uuid": {
					"$comment": "region uuid.",
					"type": "string",
					"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
				}
			},
			"required": ["uuid"],
			"additionalProperties": false
		}
	},
	additionalProperties: false
};

//#endregion
//#region src/object-model/venue-deprecated.json
var venue_deprecated_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/venue-deprecated.json",
	type: "object",
	title: "Venue Deprecated",
	objectType: "VenueDeprecated",
	$comment: "This object should always be being pulled from Dropbox by the RMS.",
	properties: {
		"storeInBlobStore": {
			"type": "boolean",
			"$comment": "Can't expose to customers. Backwards compatibility between dropbox and blob-store."
		},
		"storeSheetsInBlob": {
			"type": "boolean",
			"$comment": "Can't expose to customers. Backwards compatibility between sheets and blob-store."
		},
		"poiLabelFontName": {
			"type": "string",
			"$comment": "Account override. textStyles.canvas.poi_label.fontName"
		},
		"hasVouchers": {
			"type": "boolean",
			"$comment": "Account override. Matt says needed for Lufthansa."
		}
	},
	additionalProperties: false
};

//#endregion
//#region src/object-model/bounding-box.json
var bounding_box_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/bounding-box.json",
	type: "object",
	title: "Bounding Box",
	properties: {
		"n": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"e": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"s": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"w": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		}
	},
	required: [
		"n",
		"e",
		"s",
		"w"
	],
	constructorParameterOrder: [
		"n",
		"e",
		"s",
		"w"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/bounds-polygon.json
var bounds_polygon_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/bounds-polygon.json",
	type: "array",
	title: "Bounds Polygon",
	items: {
		"title": "LatLng Array",
		"type": "array",
		"items": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"minItems": 2,
		"maxItems": 2
	}
};

//#endregion
//#region src/object-model/category.json
var category_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/category.json",
	title: "Category",
	description: "Categories are used in the theme object to respresent theme categories of map objects.",
	definitions: { "hex-color": {
		"type": "string",
		"pattern": "^#[0-9a-fA-F]{6}$|^$"
	} },
	type: "object",
	properties: {
		"darkDesaturationFillColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"darkFillColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"darkFillColorOpacity": {
			"type": "number",
			"minimum": 0,
			"maximum": 1
		},
		"darkHaloColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"darkSelectedTextColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"darkSelectedHaloColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"darkStrokeColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"darkTextColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"desaturationFillColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"fillColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"fillColorOpacity": {
			"type": "number",
			"minimum": 0,
			"maximum": 1
		},
		"hasBadge": { "type": "boolean" },
		"haloColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"internalOnly": { "type": "boolean" },
		"selectedHaloColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"selectedTextColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"strokeColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		},
		"textColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		}
	},
	required: [],
	additionalProperties: false
};

//#endregion
//#region src/object-model/contact-information.json
var contact_information_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/contact-information.json",
	type: "object",
	title: "Contact Information",
	properties: {
		"address": {
			"type": "object",
			"title": "Address",
			"properties": {
				"street": { "type": "string" },
				"unit": { "type": "string" },
				"locality": { "type": "string" },
				"postalCode": { "type": "string" },
				"postalCodeExt": { "type": "string" },
				"province": {
					"type": "string",
					"pattern": "^[A-Z]{2}-[A-Z0-9]+$"
				},
				"country": {
					"type": "string",
					"pattern": "^[A-Z]{2}$"
				}
			},
			"required": [
				"street",
				"locality",
				"province",
				"country"
			],
			"constructorParameterOrder": [
				"street",
				"locality",
				"province",
				"country"
			],
			"additionalProperties": false
		},
		"hours": { "type": "string" },
		"phone": { "type": "string" },
		"website": { "type": "string" }
	},
	required: [],
	constructorParameterOrder: [
		"address",
		"hours",
		"phone",
		"website"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/dynamic-pois.json
var dynamic_pois_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/dynamic-pois.json",
	title: "Dynamic POIs",
	objectType: "DynamicPOIs",
	properties: {
		"hasDynamicPOIs": { "type": "boolean" },
		"hasDynamicSecurityWaitTimePOIs": { "type": "boolean" },
		"dynamicDatabaseInterval": { "type": "number" }
	},
	additionalProperties: false
};

//#endregion
//#region src/object-model/fixed-geometry.json
var fixed_geometry_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/fixed-geometry.json",
	type: "object",
	title: "Fixed Geometry",
	properties: {
		"utmData": {
			"type": "object",
			"title": "Utm Data",
			"properties": {
				"utmZone": {
					"type": "string",
					"pattern": "^[0-9]{2}(N|S)?$"
				},
				"rect": {
					"type": "object",
					"title": "Rectangle",
					"properties": {
						"x": { "type": "number" },
						"y": { "type": "number" },
						"width": {
							"type": "number",
							"min": 0
						},
						"height": {
							"type": "number",
							"min": 0
						}
					},
					"required": [
						"x",
						"y",
						"width",
						"height"
					],
					"constructorParameterOrder": [
						"x",
						"y",
						"width",
						"height"
					],
					"additionalProperties": false
				}
			},
			"required": ["utmZone", "rect"],
			"constructorParameterOrder": ["utmZone", "rect"],
			"additionalProperties": false
		},
		"viewBox": {
			"type": "object",
			"title": "Rectangle",
			"properties": {
				"x": { "type": "number" },
				"y": { "type": "number" },
				"width": {
					"type": "number",
					"min": 0
				},
				"height": {
					"type": "number",
					"min": 0
				}
			},
			"required": [
				"x",
				"y",
				"width",
				"height"
			],
			"constructorParameterOrder": [
				"x",
				"y",
				"width",
				"height"
			],
			"additionalProperties": false
		}
	},
	required: ["viewBox", "utmData"],
	constructorParameterOrder: ["viewBox", "utmData"],
	additionalProperties: false
};

//#endregion
//#region src/object-model/grab.json
var grab_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/grab.json",
	title: "Grab",
	objectType: "Grab",
	properties: {
		"enableGrab": { "type": "boolean" },
		"grabKey": {
			"type": "string",
			"pattern": "^[a-f0-9]{32}$"
		},
		"grabHost": { "type": "string" }
	},
	additionalProperties: false
};

//#endregion
//#region src/object-model/latlng.json
var latlng_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/latlng.json",
	type: "object",
	title: "LatLng",
	properties: {
		"lat": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"lng": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		}
	},
	required: ["lat", "lng"],
	constructorParameterOrder: ["lat", "lng"],
	additionalProperties: false
};

//#endregion
//#region src/object-model/latlng-array.json
var latlng_array_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/latlng-array.json",
	title: "LatLng Array",
	type: "array",
	items: {
		"type": "number",
		"minimum": -180,
		"maximum": 180
	},
	minItems: 2,
	maxItems: 2
};

//#endregion
//#region src/object-model/level.json
var level_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/level.json",
	type: "object",
	title: "Level",
	objectType: "Level",
	properties: {
		"clFloor": {
			"type": "number",
			"$comment": "Used for core location. Works similar to ordinals."
		},
		"details": { "type": "string" },
		"id": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"isDefaultForStructure": {
			"type": "boolean",
			"$comment": "Used to find default ordinal."
		},
		"name": { "type": "string" },
		"ordinalId": {
			"$comment": "Horizontal slice of the venue that this level belongs to.",
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"structureId": {
			"$comment": "The structure the level belongs to. (like a floor of a building)",
			"oneOf": [{
				"type": "string",
				"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
			}, {
				"type": "string",
				"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
			}]
		}
	},
	required: [
		"id",
		"isDefaultForStructure",
		"ordinalId",
		"structureId"
	],
	constructorParameterOrder: [
		"structureId",
		"ordinalId",
		"id",
		"name",
		"details",
		"isDefaultForStructure"
	],
	additionalProperties: false,
	filters: [
		["id"],
		["structureId"],
		["ordinalId"]
	],
	sortBy: [{ "sortBy": "ordinalId" }, { "sortBy": ["structureId", "ordinalId"] }]
};

//#endregion
//#region src/object-model/ordinal.json
var ordinal_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/ordinal.json",
	type: "object",
	title: "Ordinal",
	objectType: "Ordinal",
	properties: {
		"id": {
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"isDefaultForVenue": { "type": "boolean" },
		"ordinalNumber": { "type": "number" }
	},
	required: [
		"id",
		"isDefaultForVenue",
		"ordinalNumber"
	],
	constructorParameterOrder: [
		"id",
		"isDefaultForVenue",
		"ordinalNumber"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/places.json
var places_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/places.json",
	title: "Places",
	objectType: "Places",
	properties: {
		"places": {
			"type": "array",
			"title": "Places Array",
			"items": {
				"type": "object",
				"properties": {
					"behavior": { "type": "string" },
					"value": { "type": "string" },
					"name": { "type": "string" },
					"icon": { "type": "string" },
					"mapMarker": { "type": "string" },
					"normalColor": {
						"type": "string",
						"pattern": "^[0-9a-fA-F]{6}$"
					},
					"selectedColor": {
						"type": "string",
						"pattern": "^[0-9a-fA-F]{6}$"
					},
					"normalIconColor": {
						"type": "string",
						"pattern": "^[0-9a-fA-F]{6}$"
					},
					"selectedIconColor": {
						"type": "string",
						"pattern": "^[0-9a-fA-F]{6}$"
					},
					"cornerRadiusPercent": { "type": "number" }
				},
				"additionalProperties": false
			}
		},
		"morePlacesNormalIcon": { "type": "string" },
		"morePlacesNormalColor": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"morePlacesNormalIconColor": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"morePlacesSelectedColor": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"morePlacesSelectedIcon": { "type": "string" },
		"morePlacesSelectedIconColor": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"morePlacesCloseButtonColor": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"morePlacesCloseButtonIconColor": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"morePlacesCloseButtonIcon": { "type": "string" },
		"morePlacesCornerRadiusPercent": { "type": "number" }
	},
	definitions: { "placesArray": {
		"type": "array",
		"title": "Places Array",
		"items": {
			"type": "object",
			"properties": {
				"behavior": { "type": "string" },
				"value": { "type": "string" },
				"name": { "type": "string" },
				"icon": { "type": "string" },
				"mapMarker": { "type": "string" },
				"normalColor": {
					"type": "string",
					"pattern": "^[0-9a-fA-F]{6}$"
				},
				"selectedColor": {
					"type": "string",
					"pattern": "^[0-9a-fA-F]{6}$"
				},
				"normalIconColor": {
					"type": "string",
					"pattern": "^[0-9a-fA-F]{6}$"
				},
				"selectedIconColor": {
					"type": "string",
					"pattern": "^[0-9a-fA-F]{6}$"
				},
				"cornerRadiusPercent": { "type": "number" }
			},
			"additionalProperties": false
		}
	} },
	additionalProperties: false
};

//#endregion
//#region src/object-model/rectangle.json
var rectangle_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/rectangle.json",
	type: "object",
	title: "Rectangle",
	properties: {
		"x": { "type": "number" },
		"y": { "type": "number" },
		"width": {
			"type": "number",
			"min": 0
		},
		"height": {
			"type": "number",
			"min": 0
		}
	},
	required: [
		"x",
		"y",
		"width",
		"height"
	],
	constructorParameterOrder: [
		"x",
		"y",
		"width",
		"height"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/waiting-lane-groups.json
var waiting_lane_groups_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/waiting-lane-groups.json",
	title: "Waiting Lane Groups",
	objectType: "WaitingLaneGroups",
	type: "object",
	properties: {
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"waitingLaneGroups": {
			"$type": "array",
			"items": {
				"type": "object",
				"title": "Waiting Lane Group",
				"properties": {
					"lane": {
						"type": "string",
						"enum": ["SecurityLane", "ImmigrationLane"]
					},
					"alternatives": {
						"type": "array",
						"items": {
							"type": "object",
							"title": "Waiting Lane Alternative",
							"properties": {
								"laneName": {
									"type": "string",
									"enum": [
										"general",
										"tsapre",
										"clear",
										"visitor",
										"resident",
										"globalEntry",
										"skyPriority",
										"fastTrack",
										"locusPriority",
										"priority"
									]
								},
								"displayText": { "type": "string" },
								"isDefaultVariation": { "type": "boolean" }
							},
							"required": [
								"laneName",
								"displayText",
								"isDefaultVariation"
							],
							"additionalProperties": false
						}
					}
				},
				"required": ["lane", "alternatives"],
				"additionalProperties": false
			}
		}
	},
	required: ["venueId", "waitingLaneGroups"],
	additionalProperties: false,
	definitions: {
		"waitingLaneGroup": {
			"type": "object",
			"title": "Waiting Lane Group",
			"properties": {
				"lane": {
					"type": "string",
					"enum": ["SecurityLane", "ImmigrationLane"]
				},
				"alternatives": {
					"type": "array",
					"items": {
						"type": "object",
						"title": "Waiting Lane Alternative",
						"properties": {
							"laneName": {
								"type": "string",
								"enum": [
									"general",
									"tsapre",
									"clear",
									"visitor",
									"resident",
									"globalEntry",
									"skyPriority",
									"fastTrack",
									"locusPriority",
									"priority"
								]
							},
							"displayText": { "type": "string" },
							"isDefaultVariation": { "type": "boolean" }
						},
						"required": [
							"laneName",
							"displayText",
							"isDefaultVariation"
						],
						"additionalProperties": false
					}
				}
			},
			"required": ["lane", "alternatives"],
			"additionalProperties": false
		},
		"waitingLaneAlternative": {
			"type": "object",
			"title": "Waiting Lane Alternative",
			"properties": {
				"laneName": {
					"type": "string",
					"enum": [
						"general",
						"tsapre",
						"clear",
						"visitor",
						"resident",
						"globalEntry",
						"skyPriority",
						"fastTrack",
						"locusPriority",
						"priority"
					]
				},
				"displayText": { "type": "string" },
				"isDefaultVariation": { "type": "boolean" }
			},
			"required": [
				"laneName",
				"displayText",
				"isDefaultVariation"
			],
			"additionalProperties": false
		}
	}
};

//#endregion
//#region src/object-model/string-patterns.json
var string_patterns_default$2 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/string-patterns.json",
	title: "String Patterns",
	definitions: {
		"align": {
			"type": "string",
			"enum": [
				"left",
				"center",
				"right"
			]
		},
		"categoryName": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"color": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$"
		},
		"colorNoHash": {
			"type": "string",
			"pattern": "^[0-9a-fA-F]{6}$"
		},
		"dateTime": {
			"type": "string",
			"format": "date-time"
		},
		"featureId": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"locale": {
			"type": "string",
			"pattern": "^[a-zA-Z_-]+$"
		},
		"md5": {
			"type": "string",
			"pattern": "^[a-f0-9]{32}$"
		},
		"numberString": {
			"type": "string",
			"pattern": "^[-+]?(?:\\d*\\.\\d+|\\d+\\.?)$"
		},
		"ordinalId": {
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"strokeAlign": {
			"type": "string",
			"enum": [
				"inside",
				"center",
				"outside"
			]
		},
		"structureId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
		}] },
		"subid": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"utmZone": {
			"type": "string",
			"pattern": "^[0-9]{2}(N|S)?$"
		},
		"uuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"version": {
			"type": "string",
			"pattern": "20[0-9]{2}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}$"
		},
		"vmsUuid": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9]+_[0-9]+$"
		},
		"entityId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"waitingLane": {
			"type": "string",
			"enum": [
				"general",
				"tsapre",
				"clear",
				"visitor",
				"resident",
				"globalEntry",
				"skyPriority",
				"fastTrack",
				"locusPriority",
				"priority"
			]
		}
	}
};

//#endregion
//#region src/object-model/structure.json
var structure_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/structure.json",
	type: "object",
	title: "Structure",
	objectType: "Structure",
	properties: {
		"id": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
		}] },
		"isDefaultForVenue": {
			"type": "boolean",
			"$comment": "Some venues have no default. Used to find default ordinal."
		},
		"name": { "type": "string" },
		"shouldDisplay": {
			"type": "boolean",
			"$comment": "Believe that this is whether the structures information(like name) is displayed on the map. Undefined and false mean different things."
		},
		"selectorOrder": {
			"type": "integer",
			"minimum": 0,
			"$comment": "dropdown order"
		},
		"structureOrder": {
			"type": "integer",
			"minimum": 0,
			"$comment": "drawing order"
		}
	},
	required: [
		"id",
		"isDefaultForVenue",
		"structureOrder"
	],
	constructorParameterOrder: [
		"id",
		"isDefaultForVenue",
		"name",
		"structureOrder",
		"selectorOrder",
		"shouldDisplay"
	],
	additionalProperties: false,
	filters: [
		["id"],
		["structureOrder"],
		["selectorOrder"]
	],
	sortBy: [
		{ "sortBy": "selectorOrder" },
		{ "sortBy": "structureOrder" },
		{ "sortBy": "id" }
	]
};

//#endregion
//#region src/object-model/theme.json
var theme_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/theme.json",
	title: "Theme",
	type: "object",
	description: "Categories should be split into by keys.",
	patternProperties: { "^[a-zA-Z.]+$": {
		"title": "Category",
		"description": "Categories are used in the theme object to respresent theme categories of map objects.",
		"definitions": { "hex-color": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$|^$"
		} },
		"type": "object",
		"properties": {
			"darkDesaturationFillColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"darkFillColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"darkFillColorOpacity": {
				"type": "number",
				"minimum": 0,
				"maximum": 1
			},
			"darkHaloColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"darkSelectedTextColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"darkSelectedHaloColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"darkStrokeColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"darkTextColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"desaturationFillColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"fillColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"fillColorOpacity": {
				"type": "number",
				"minimum": 0,
				"maximum": 1
			},
			"hasBadge": { "type": "boolean" },
			"haloColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"internalOnly": { "type": "boolean" },
			"selectedHaloColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"selectedTextColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"strokeColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			},
			"textColor": {
				"type": "string",
				"pattern": "^#[0-9a-fA-F]{6}$|^$"
			}
		},
		"required": [],
		"additionalProperties": false
	} },
	additionalProperties: false
};

//#endregion
//#region src/object-model/utm-data.json
var utm_data_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/utm-data.json",
	type: "object",
	title: "Utm Data",
	properties: {
		"utmZone": {
			"type": "string",
			"pattern": "^[0-9]{2}(N|S)?$"
		},
		"rect": {
			"type": "object",
			"title": "Rectangle",
			"properties": {
				"x": { "type": "number" },
				"y": { "type": "number" },
				"width": {
					"type": "number",
					"min": 0
				},
				"height": {
					"type": "number",
					"min": 0
				}
			},
			"required": [
				"x",
				"y",
				"width",
				"height"
			],
			"constructorParameterOrder": [
				"x",
				"y",
				"width",
				"height"
			],
			"additionalProperties": false
		}
	},
	required: ["utmZone", "rect"],
	constructorParameterOrder: ["utmZone", "rect"],
	additionalProperties: false
};

//#endregion
//#region src/object-model/venue.json
var venue_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/venue.json",
	type: "object",
	title: "Venue",
	objectType: "Venue",
	properties: {
		"assetFormatVersion": {
			"type": "string",
			"$comment": "Currently used to distingush, which venues are V5."
		},
		"contactInformation": {
			"type": "object",
			"title": "Contact Information",
			"properties": {
				"address": {
					"type": "object",
					"title": "Address",
					"properties": {
						"street": { "type": "string" },
						"unit": { "type": "string" },
						"locality": { "type": "string" },
						"postalCode": { "type": "string" },
						"postalCodeExt": { "type": "string" },
						"province": {
							"type": "string",
							"pattern": "^[A-Z]{2}-[A-Z0-9]+$"
						},
						"country": {
							"type": "string",
							"pattern": "^[A-Z]{2}$"
						}
					},
					"required": [
						"street",
						"locality",
						"province",
						"country"
					],
					"constructorParameterOrder": [
						"street",
						"locality",
						"province",
						"country"
					],
					"additionalProperties": false
				},
				"hours": { "type": "string" },
				"phone": { "type": "string" },
				"website": { "type": "string" }
			},
			"required": [],
			"constructorParameterOrder": [
				"address",
				"hours",
				"phone",
				"website"
			],
			"additionalProperties": false
		},
		"details": { "type": "string" },
		"defaultOrdinal": {
			"type": "integer",
			"$comment": "Denotes default ordinal. Often overriden by clients when default structure is available."
		},
		"fixedGeometry": {
			"$comment": "Can derive 'viewBox', 'utmBounds' and 'bounds'. utm: universal transverse mercator projection (note: not spherical coords like lat,lng)",
			"type": "object",
			"title": "Fixed Geometry",
			"properties": {
				"utmData": {
					"type": "object",
					"title": "Utm Data",
					"properties": {
						"utmZone": {
							"type": "string",
							"pattern": "^[0-9]{2}(N|S)?$"
						},
						"rect": {
							"type": "object",
							"title": "Rectangle",
							"properties": {
								"x": { "type": "number" },
								"y": { "type": "number" },
								"width": {
									"type": "number",
									"min": 0
								},
								"height": {
									"type": "number",
									"min": 0
								}
							},
							"required": [
								"x",
								"y",
								"width",
								"height"
							],
							"constructorParameterOrder": [
								"x",
								"y",
								"width",
								"height"
							],
							"additionalProperties": false
						}
					},
					"required": ["utmZone", "rect"],
					"constructorParameterOrder": ["utmZone", "rect"],
					"additionalProperties": false
				},
				"viewBox": {
					"type": "object",
					"title": "Rectangle",
					"properties": {
						"x": { "type": "number" },
						"y": { "type": "number" },
						"width": {
							"type": "number",
							"min": 0
						},
						"height": {
							"type": "number",
							"min": 0
						}
					},
					"required": [
						"x",
						"y",
						"width",
						"height"
					],
					"constructorParameterOrder": [
						"x",
						"y",
						"width",
						"height"
					],
					"additionalProperties": false
				}
			},
			"required": ["viewBox", "utmData"],
			"constructorParameterOrder": ["viewBox", "utmData"],
			"additionalProperties": false
		},
		"id": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"locale": {
			"$comment": "The locale of the venue. 'en' means it is not a locale override.",
			"type": "string",
			"pattern": "^[a-zA-Z_-]+$"
		},
		"name": { "type": "string" },
		"tz": {
			"type": "string",
			"$comment": "The timezome of the venue."
		},
		"venueCategory": {
			"type": "string",
			"$comment": "The venue type. Airport, Casino, Smartcampus, etc."
		},
		"venueCenter": {
			"$comment": "Center of the venue in lat,lng.",
			"title": "LatLng Array",
			"type": "array",
			"items": {
				"type": "number",
				"minimum": -180,
				"maximum": 180
			},
			"minItems": 2,
			"maxItems": 2
		},
		"venueCode": {
			"type": "string",
			"$comment": "Usually same as id just uppercase."
		},
		"venueRadius": {
			"type": "number",
			"min": 1,
			"$comment": "The radius of the venue. Not sure of metric type."
		}
	},
	required: [
		"assetFormatVersion",
		"contactInformation",
		"details",
		"fixedGeometry",
		"id",
		"locale",
		"name",
		"venueCategory",
		"venueCenter",
		"venueCode"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/wayfinding-segment.json
var wayfinding_segment_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/wayfindingSegment.json",
	type: "object",
	title: "Wayfinding Segment",
	definitions: {
		"lng": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"lat": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"segmentType": {
			"type": "string",
			"enum": [
				"walk",
				"security",
				"escalator"
			]
		},
		"lngLatArray": {
			"type": "array",
			"items": [{
				"type": "number",
				"minimum": -180,
				"maximum": 180
			}, {
				"type": "number",
				"minimum": -90,
				"maximum": 90
			}]
		},
		"lineString": {
			"type": "array",
			"items": {
				"type": "array",
				"items": [{
					"type": "number",
					"minimum": -180,
					"maximum": 180
				}, {
					"type": "number",
					"minimum": -90,
					"maximum": 90
				}]
			}
		},
		"badge": {
			"type": "object",
			"properties": {
				"entityId": {
					"type": "string",
					"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
				},
				"canonicalName": {
					"type": "string",
					"pattern": "^[a-z.]+$"
				},
				"coordinates": {
					"type": "array",
					"items": [{
						"type": "number",
						"minimum": -180,
						"maximum": 180
					}, {
						"type": "number",
						"minimum": -90,
						"maximum": 90
					}]
				}
			},
			"required": [
				"entityId",
				"canonicalName",
				"coordinates"
			],
			"additionalProperties": false
		}
	},
	properties: {
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"ordinalId": {
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"coordinates": {
			"type": "array",
			"items": {
				"type": "array",
				"items": [{
					"type": "number",
					"minimum": -180,
					"maximum": 180
				}, {
					"type": "number",
					"minimum": -90,
					"maximum": 90
				}]
			}
		},
		"segmentType": {
			"type": "string",
			"enum": [
				"walk",
				"security",
				"escalator"
			]
		},
		"badges": {
			"type": "array",
			"items": {
				"type": "object",
				"properties": {
					"entityId": {
						"type": "string",
						"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
					},
					"canonicalName": {
						"type": "string",
						"pattern": "^[a-z.]+$"
					},
					"coordinates": {
						"type": "array",
						"items": [{
							"type": "number",
							"minimum": -180,
							"maximum": 180
						}, {
							"type": "number",
							"minimum": -90,
							"maximum": 90
						}]
					}
				},
				"required": [
					"entityId",
					"canonicalName",
					"coordinates"
				],
				"additionalProperties": false
			}
		}
	},
	required: [
		"levelId",
		"ordinalId",
		"venueId",
		"segmentType",
		"coordinates"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/image.json
var image_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/image.json",
	title: "Image",
	type: "object",
	objectType: "image",
	description: "Defines an image.",
	properties: {
		"blobId": {
			"type": "string",
			"minLength": 1
		},
		"type": {
			"type": "string",
			"enum": ["logo", "image"]
		},
		"orderIndex": {
			"type": "integer",
			"minimum": 0
		},
		"caption": { "type": "string" },
		"entityId": {
			"type": "string",
			"minLength": 1
		}
	},
	required: [
		"blobId",
		"type",
		"entityId",
		"orderIndex"
	],
	filters: [["entityId"]],
	sortBy: [{
		"filter": ["entityId"],
		"sortBy": "orderIndex"
	}, { "sortBy": "orderIndex" }]
};

//#endregion
//#region src/object-model/AiJsonReference.json
var AiJsonReference_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/AiJsonReference.json",
	type: "object",
	title: "Ai Json Reference",
	objectType: "AiJsonReference",
	description: "Defines a reference to aijson in the blob store",
	properties: {
		"blobId": {
			"type": "string",
			"minLength": 1
		},
		"formatVersion": {
			"type": "string",
			"enum": ["v4", "v5"]
		}
	},
	required: ["blobId", "formatVersion"],
	filters: [["formatVersion"]]
};

//#endregion
//#region src/object-model/poiSheetReference.json
var poiSheetReference_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/poiSheetReference.json",
	type: "object",
	title: "Poi Sheet Reference",
	objectType: "poiSheetReference",
	description: "Defines a reference to a poi sheet blob stored in the blob store.",
	properties: {
		"blobId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}\\-[0-9a-fA-F]{4}\\-[0-9a-fA-F]{4}\\-[0-9a-fA-F]{4}\\-[0-9a-fA-F]{12}"
		},
		"lastPush": {
			"type": "string",
			"format": "date-time"
		},
		"lastPull": {
			"type": "string",
			"format": "date-time"
		},
		"sheetType": {
			"type": "string",
			"enum": [
				"MAIN",
				"-kw",
				"-o",
				"EXTENDED"
			]
		}
	},
	filters: [["sheetType"]],
	required: ["blobId", "sheetType"]
};

//#endregion
//#region src/object-model/navGraphReference.json
var navGraphReference_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/navGraphReference.json",
	title: "Nav Grap Reference",
	type: "object",
	objectType: "navGraphReference",
	description: "Defines a reference to a nav graph blob stored in the blob store.",
	properties: { "blobId": {
		"type": "string",
		"minLength": 1
	} },
	required: ["blobId"]
};

//#endregion
//#region src/object-model/entity.json
var entity_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/entity.json",
	title: "Entity",
	type: "object",
	objectType: "Entity",
	definitions: {
		"state-machine-state": {
			"type": "string",
			"enum": ["open", "permanently-closed"],
			"default": "open",
			"$comment": "might require further discussion. if its really only going to be 1 of these two options, might just be better as a boolean"
		},
		"displayProperties": { "properties": { "suppressedUiElements": {
			"type": "array",
			"items": {
				"type": "string",
				"enum": ["directionsButton", "poiWindow"]
			}
		} } },
		"securityCheckpoint": { "properties": {
			"isSecurityCheckpoint": {
				"type": "boolean",
				"default": false,
				"$comment": "Denotes whether or not this entity is a security checkpoint"
			},
			"checkpointId": {
				"type": "string",
				"description": "Ties multiple security lanes together.  This is the same as the idIndexWithinVenue of the associated 'General Lane' for this checkpoint."
			},
			"securityLane": {
				"type": "string",
				"enum": [
					"general",
					"tsapre",
					"clear",
					"visitor",
					"resident",
					"globalEntry",
					"skyPriority",
					"fastTrack",
					"locusPriority",
					"priority"
				]
			},
			"immigrationLane": {
				"type": "string",
				"enum": [
					"general",
					"tsapre",
					"clear",
					"visitor",
					"resident",
					"globalEntry",
					"skyPriority",
					"fastTrack",
					"locusPriority",
					"priority"
				]
			}
		} }
	},
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"idIndexWithinVenue": {
			"type": "number",
			"minimum": 1
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"name": {
			"type": "string",
			"minLength": 1
		},
		"category": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"state": {
			"type": "string",
			"enum": ["open", "permanently-closed"],
			"default": "open",
			"$comment": "might require further discussion. if its really only going to be 1 of these two options, might just be better as a boolean"
		},
		"tags": {
			"type": "array",
			"items": {
				"type": "object",
				"title": "Tag",
				"properties": {
					"label": { "type": "string" },
					"isHidden": {
						"type": "boolean",
						"default": false
					}
				},
				"required": ["label"],
				"constructorParameterOrder": ["label"],
				"additionalProperties": false
			},
			"default": [],
			"$comment": "Contains display tags and normal tags"
		},
		"searchTerms": {
			"type": "array",
			"items": {
				"type": "object",
				"title": "Search Term",
				"properties": {
					"type": {
						"type": "string",
						"enum": [
							"airline",
							"category",
							"custom"
						]
					},
					"value": { "type": "string" }
				},
				"required": ["type", "value"],
				"constructorParameterOrder": ["type", "value"],
				"additionalProperties": false
			},
			"default": [],
			"$comment": "This should contain airline, categorySearch, and customSearch from google sheets"
		},
		"isInsideSecurity": {
			"type": "boolean",
			"default": false,
			"$comment": "Denotes whether or not you must meet security requirements to reach this entity"
		},
		"isSearchable": {
			"type": "boolean",
			"default": true,
			"$comment": "When false this Entity should not appear in search results. We figure it is currently most relevant for sections."
		},
		"description": {
			"type": "string",
			"minLength": 1
		},
		"portalStatus": {
			"type": "string",
			"enum": ["empty", "unknown"],
			"$comment": "Only used for portals. Empty means that the portal doesn't lead anywhere on the map (e.g. service elevator), unknown means we need to go into the field to check where it goes."
		},
		"contactInformation": {
			"type": "object",
			"title": "Contact Information",
			"properties": {
				"address": {
					"type": "object",
					"title": "Address",
					"properties": {
						"street": { "type": "string" },
						"unit": { "type": "string" },
						"locality": { "type": "string" },
						"postalCode": { "type": "string" },
						"postalCodeExt": { "type": "string" },
						"province": {
							"type": "string",
							"pattern": "^[A-Z]{2}-[A-Z0-9]+$"
						},
						"country": {
							"type": "string",
							"pattern": "^[A-Z]{2}$"
						}
					},
					"required": [
						"street",
						"locality",
						"province",
						"country"
					],
					"constructorParameterOrder": [
						"street",
						"locality",
						"province",
						"country"
					],
					"additionalProperties": false
				},
				"hours": { "type": "string" },
				"phone": { "type": "string" },
				"website": { "type": "string" }
			},
			"required": [],
			"constructorParameterOrder": [
				"address",
				"hours",
				"phone",
				"website"
			],
			"additionalProperties": false
		},
		"nearbyLandmark": {
			"type": "string",
			"$comment": "In sheets, this property is gate"
		},
		"nearbySection": {
			"type": "string",
			"$comment": "In sheets, this property is terminal"
		},
		"links": {
			"type": "array",
			"items": {
				"type": "object",
				"title": "Link",
				"properties": {
					"label": { "type": "string" },
					"type": {
						"type": "string",
						"enum": [
							"primary",
							"menu",
							"shop",
							"parking",
							"mobileordering"
						]
					},
					"uri": {
						"type": "string",
						"format": "uri-reference"
					}
				},
				"required": ["uri"],
				"constructorParameterOrder": [
					"uri",
					"type",
					"label"
				],
				"additionalProperties": false
			},
			"$comment": "should contain primary, menu, shop, parking, and mobileordering"
		},
		"displayProperties": { "properties": { "suppressedUiElements": {
			"type": "array",
			"items": {
				"type": "string",
				"enum": ["directionsButton", "poiWindow"]
			}
		} } },
		"geofence": {
			"type": "string",
			"description": "A name of a geofence which, if the end-user of the map is within said geofence, tells the SDK to present the end-user with additional features"
		},
		"securityCheckpoint": { "properties": {
			"isSecurityCheckpoint": {
				"type": "boolean",
				"default": false,
				"$comment": "Denotes whether or not this entity is a security checkpoint"
			},
			"checkpointId": {
				"type": "string",
				"description": "Ties multiple security lanes together.  This is the same as the idIndexWithinVenue of the associated 'General Lane' for this checkpoint."
			},
			"securityLane": {
				"type": "string",
				"enum": [
					"general",
					"tsapre",
					"clear",
					"visitor",
					"resident",
					"globalEntry",
					"skyPriority",
					"fastTrack",
					"locusPriority",
					"priority"
				]
			},
			"immigrationLane": {
				"type": "string",
				"enum": [
					"general",
					"tsapre",
					"clear",
					"visitor",
					"resident",
					"globalEntry",
					"skyPriority",
					"fastTrack",
					"locusPriority",
					"priority"
				]
			}
		} },
		"roomId": {
			"type": "string",
			"description": "The human readable identifier of a unit that appears in a customer's database"
		},
		"preferenceId": {
			"type": "string",
			"description": "An identifier that caesars uses to map our entities to theirs"
		},
		"roomResourceName": {
			"type": "string",
			"description": "A reference to a room in a customer's database for use in conference room booking."
		},
		"amenities": {
			"type": "array",
			"description": "The human readable features/amenities of an entity"
		},
		"capacity": {
			"type": "array",
			"title": "Capacity",
			"minItems": 1,
			"maxItems": 2,
			"uniqueItems": true,
			"items": {
				"type": "integer",
				"minimum": 0,
				"exclusiveMinimum": 0,
				"$comment": "This seems redundant, but is here because of the object store implementation.  It has an odd mishmash of schema04 and schema07."
			},
			"description": "The unitless capacity of a room.  Numbers are inclusive",
			"constructorParameterOrder": [],
			"additionalProperties": false
		},
		"pinned": {
			"type": "boolean",
			"description": "If this entity should be pinned."
		},
		"priority": {
			"type": "number",
			"description": "The priority level of this entity."
		},
		"additionalLogo": {
			"type": "string",
			"$comment": "Used for LHR specifically -- we have the normal 'logo' attribute, but LHR might be expecting it in a different place."
		},
		"journeyPlanner": {
			"type": "string",
			"description": "Used by LHR's Journey Planner Feature."
		}
	},
	required: [
		"id",
		"idIndexWithinVenue",
		"venueId",
		"name",
		"category",
		"state",
		"tags",
		"searchTerms",
		"isInsideSecurity",
		"isSearchable"
	],
	constructorParameterOrder: [
		"id",
		"idIndexWithinVenue",
		"venueId",
		"name",
		"category",
		"state",
		"tags",
		"searchTerms",
		"isInsideSecurity",
		"isSearchable",
		"description",
		"displayTags",
		"portalStatus",
		"contactInformation",
		"nearbyLandmark",
		"nearbySection",
		"links",
		"displayProperties",
		"securityCheckpoint",
		"geofence",
		"roomId",
		"preferenceId",
		"roomResourceName",
		"amenities",
		"capacity",
		"pinned",
		"priority",
		"additionalLogo",
		"journeyPlanner"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/entity-override.json
var entity_override_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/entity-override.json",
	title: "Entity override data",
	$comment: "Covers both overrides by account, and overrides by locale",
	type: "object",
	objectType: "EntityOverride",
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"entityId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"name": {
			"type": "string",
			"minLength": 1
		},
		"description": {
			"type": "string",
			"minLength": 1
		},
		"nearbySection": {
			"type": "string",
			"$comment": "In sheets, this property is terminal"
		},
		"nearbyLandmark": {
			"type": "string",
			"$comment": "In sheets, this property is gate"
		},
		"tags": {
			"type": "array",
			"items": {
				"type": "object",
				"title": "Tag",
				"properties": {
					"label": { "type": "string" },
					"isHidden": {
						"type": "boolean",
						"default": false
					}
				},
				"required": ["label"],
				"constructorParameterOrder": ["label"],
				"additionalProperties": false
			}
		},
		"links": {
			"type": "array",
			"items": {
				"type": "object",
				"title": "Link",
				"properties": {
					"label": { "type": "string" },
					"type": {
						"type": "string",
						"enum": [
							"primary",
							"menu",
							"shop",
							"parking",
							"mobileordering"
						]
					},
					"uri": {
						"type": "string",
						"format": "uri-reference"
					}
				},
				"required": ["uri"],
				"constructorParameterOrder": [
					"uri",
					"type",
					"label"
				],
				"additionalProperties": false
			},
			"$comment": "could contain primary, menu, shop, parking, and mobileordering"
		},
		"searchTerms": {
			"type": "array",
			"items": {
				"type": "object",
				"title": "Search Term",
				"properties": {
					"type": {
						"type": "string",
						"enum": [
							"airline",
							"category",
							"custom"
						]
					},
					"value": { "type": "string" }
				},
				"required": ["type", "value"],
				"constructorParameterOrder": ["type", "value"],
				"additionalProperties": false
			},
			"$comment": "This should contain airline, categorySearch, and customSearch from google sheets"
		},
		"dynamicIntegration": {
			"title": "Dynamic Integration",
			"oneOf": [
				{
					"type": "object",
					"properties": {
						"integrationType": {
							"type": "string",
							"enum": ["open-closed-poi"]
						},
						"timeToLive": { "type": "number" },
						"clientId": { "type": "string" }
					},
					"required": [
						"timeToLive",
						"clientId",
						"integrationType"
					],
					"additionalProperties": false
				},
				{
					"type": "object",
					"properties": {
						"integrationType": {
							"type": "string",
							"enum": ["nav"]
						},
						"default": { "type": "string" },
						"integrationUrl": {
							"type": "string",
							"format": "uri-reference"
						},
						"timeToLive": { "type": "number" },
						"clientId": { "type": "string" },
						"property": { "type": "string" },
						"resultId": { "type": "string" },
						"checkpointId": { "type": "string" }
					},
					"required": ["timeToLive", "default"],
					"additionalProperties": false
				},
				{
					"type": "object",
					"properties": {
						"integrationType": {
							"type": "string",
							"enum": ["parking"]
						},
						"default": { "type": "string" },
						"integrationUrl": {
							"type": "string",
							"format": "uri-reference"
						},
						"timeToLive": { "type": "number" },
						"clientId": { "type": "string" },
						"property": { "type": "string" },
						"resultId": { "type": "string" },
						"lot": { "type": "string" }
					},
					"required": ["timeToLive", "default"],
					"additionalProperties": false
				}
			]
		}
	},
	required: ["id", "entityId"],
	constructorParameterOrder: [
		"id",
		"entityId",
		"venueId",
		"name",
		"description",
		"nearbySection",
		"nearbyLandmark",
		"tags",
		"links",
		"searchTerms",
		"dynamicIntegration"
	]
};

//#endregion
//#region src/object-model/entity-deprecated.json
var entity_deprecated_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/entity-v4-legacy.json",
	title: "Entity Pixi Legacy",
	type: "object",
	objectType: "EntityDeprecated",
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"entityId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"floor": { "type": "string" },
		"poiCategory": { "type": "string" },
		"airport": { "type": "string" },
		"icon": {
			"type": "string",
			"format": "uri-reference",
			"$comment": "Used by the android and iOS Sdks for displaying an icon in the POI window, not the map"
		},
		"gateType": {
			"type": "string",
			"enum": [
				"departure",
				"arrival",
				"departure-arrival"
			],
			"$comment": "maybe not necessary anymore, given the current categories"
		},
		"sectionId": { "type": "string" },
		"showMarker": { "type": "boolean" }
	},
	required: ["entityId"],
	constructorParameterOrder: [
		"id",
		"entityId",
		"floor",
		"poiCategory",
		"airport",
		"icon",
		"sectionId",
		"gateType",
		"showMarker"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/customer-specific/A1B4XSN6HDHIR2-entity-override.json
var A1B4XSN6HDHIR2_entity_override_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/customer-specific/A1B4XSN6HDHIR2-entity-override.json",
	title: "A1B4XSN6HDHIR2 Entity Override",
	type: "object",
	objectType: "EntityOverride",
	allOf: [{
		"title": "Entity override data",
		"$comment": "Covers both overrides by account, and overrides by locale",
		"type": "object",
		"objectType": "EntityOverride",
		"properties": {
			"id": {
				"type": "string",
				"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
			},
			"entityId": {
				"type": "string",
				"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
			},
			"name": {
				"type": "string",
				"minLength": 1
			},
			"description": {
				"type": "string",
				"minLength": 1
			},
			"nearbySection": {
				"type": "string",
				"$comment": "In sheets, this property is terminal"
			},
			"nearbyLandmark": {
				"type": "string",
				"$comment": "In sheets, this property is gate"
			},
			"tags": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Tag",
					"properties": {
						"label": { "type": "string" },
						"isHidden": {
							"type": "boolean",
							"default": false
						}
					},
					"required": ["label"],
					"constructorParameterOrder": ["label"],
					"additionalProperties": false
				}
			},
			"links": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Link",
					"properties": {
						"label": { "type": "string" },
						"type": {
							"type": "string",
							"enum": [
								"primary",
								"menu",
								"shop",
								"parking",
								"mobileordering"
							]
						},
						"uri": {
							"type": "string",
							"format": "uri-reference"
						}
					},
					"required": ["uri"],
					"constructorParameterOrder": [
						"uri",
						"type",
						"label"
					],
					"additionalProperties": false
				},
				"$comment": "could contain primary, menu, shop, parking, and mobileordering"
			},
			"searchTerms": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Search Term",
					"properties": {
						"type": {
							"type": "string",
							"enum": [
								"airline",
								"category",
								"custom"
							]
						},
						"value": { "type": "string" }
					},
					"required": ["type", "value"],
					"constructorParameterOrder": ["type", "value"],
					"additionalProperties": false
				},
				"$comment": "This should contain airline, categorySearch, and customSearch from google sheets"
			},
			"dynamicIntegration": {
				"title": "Dynamic Integration",
				"oneOf": [
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["open-closed-poi"]
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" }
						},
						"required": [
							"timeToLive",
							"clientId",
							"integrationType"
						],
						"additionalProperties": false
					},
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["nav"]
							},
							"default": { "type": "string" },
							"integrationUrl": {
								"type": "string",
								"format": "uri-reference"
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" },
							"property": { "type": "string" },
							"resultId": { "type": "string" },
							"checkpointId": { "type": "string" }
						},
						"required": ["timeToLive", "default"],
						"additionalProperties": false
					},
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["parking"]
							},
							"default": { "type": "string" },
							"integrationUrl": {
								"type": "string",
								"format": "uri-reference"
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" },
							"property": { "type": "string" },
							"resultId": { "type": "string" },
							"lot": { "type": "string" }
						},
						"required": ["timeToLive", "default"],
						"additionalProperties": false
					}
				]
			}
		},
		"required": ["id", "entityId"],
		"constructorParameterOrder": [
			"id",
			"entityId",
			"venueId",
			"name",
			"description",
			"nearbySection",
			"nearbyLandmark",
			"tags",
			"links",
			"searchTerms",
			"dynamicIntegration"
		]
	}, {
		"properties": { "checkin": { "type": "string" } },
		"required": []
	}]
};

//#endregion
//#region src/object-model/customer-specific/A13468QJ9MG74M-entity-override.json
var A13468QJ9MG74M_entity_override_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/customer-specific/A13468QJ9MG74M-entity-override.json",
	title: "A13468QJ9MG74M Entity Override",
	type: "object",
	objectType: "EntityOverride",
	allOf: [{
		"title": "Entity override data",
		"$comment": "Covers both overrides by account, and overrides by locale",
		"type": "object",
		"objectType": "EntityOverride",
		"properties": {
			"id": {
				"type": "string",
				"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
			},
			"entityId": {
				"type": "string",
				"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
			},
			"name": {
				"type": "string",
				"minLength": 1
			},
			"description": {
				"type": "string",
				"minLength": 1
			},
			"nearbySection": {
				"type": "string",
				"$comment": "In sheets, this property is terminal"
			},
			"nearbyLandmark": {
				"type": "string",
				"$comment": "In sheets, this property is gate"
			},
			"tags": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Tag",
					"properties": {
						"label": { "type": "string" },
						"isHidden": {
							"type": "boolean",
							"default": false
						}
					},
					"required": ["label"],
					"constructorParameterOrder": ["label"],
					"additionalProperties": false
				}
			},
			"links": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Link",
					"properties": {
						"label": { "type": "string" },
						"type": {
							"type": "string",
							"enum": [
								"primary",
								"menu",
								"shop",
								"parking",
								"mobileordering"
							]
						},
						"uri": {
							"type": "string",
							"format": "uri-reference"
						}
					},
					"required": ["uri"],
					"constructorParameterOrder": [
						"uri",
						"type",
						"label"
					],
					"additionalProperties": false
				},
				"$comment": "could contain primary, menu, shop, parking, and mobileordering"
			},
			"searchTerms": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Search Term",
					"properties": {
						"type": {
							"type": "string",
							"enum": [
								"airline",
								"category",
								"custom"
							]
						},
						"value": { "type": "string" }
					},
					"required": ["type", "value"],
					"constructorParameterOrder": ["type", "value"],
					"additionalProperties": false
				},
				"$comment": "This should contain airline, categorySearch, and customSearch from google sheets"
			},
			"dynamicIntegration": {
				"title": "Dynamic Integration",
				"oneOf": [
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["open-closed-poi"]
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" }
						},
						"required": [
							"timeToLive",
							"clientId",
							"integrationType"
						],
						"additionalProperties": false
					},
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["nav"]
							},
							"default": { "type": "string" },
							"integrationUrl": {
								"type": "string",
								"format": "uri-reference"
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" },
							"property": { "type": "string" },
							"resultId": { "type": "string" },
							"checkpointId": { "type": "string" }
						},
						"required": ["timeToLive", "default"],
						"additionalProperties": false
					},
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["parking"]
							},
							"default": { "type": "string" },
							"integrationUrl": {
								"type": "string",
								"format": "uri-reference"
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" },
							"property": { "type": "string" },
							"resultId": { "type": "string" },
							"lot": { "type": "string" }
						},
						"required": ["timeToLive", "default"],
						"additionalProperties": false
					}
				]
			}
		},
		"required": ["id", "entityId"],
		"constructorParameterOrder": [
			"id",
			"entityId",
			"venueId",
			"name",
			"description",
			"nearbySection",
			"nearbyLandmark",
			"tags",
			"links",
			"searchTerms",
			"dynamicIntegration"
		]
	}, {
		"properties": { "voucher": { "type": "string" } },
		"required": []
	}],
	constructorParameterOrder: [
		"entityId",
		"venueId",
		"name",
		"description",
		"nearbySection",
		"nearbyLandmark",
		"tags",
		"links",
		"searchTerms",
		"dynamicIntegration"
	]
};

//#endregion
//#region src/object-model/customer-specific/A1GHMDY8PFDNUA-entity-override.json
var A1GHMDY8PFDNUA_entity_override_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/customer-specific/A1GHMDY8PFDNUA-entity-override.json",
	title: "A1GHMDY8PFDNUA Entity Override",
	type: "object",
	objectType: "EntityOverride",
	allOf: [{
		"title": "Entity override data",
		"$comment": "Covers both overrides by account, and overrides by locale",
		"type": "object",
		"objectType": "EntityOverride",
		"properties": {
			"id": {
				"type": "string",
				"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
			},
			"entityId": {
				"type": "string",
				"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
			},
			"name": {
				"type": "string",
				"minLength": 1
			},
			"description": {
				"type": "string",
				"minLength": 1
			},
			"nearbySection": {
				"type": "string",
				"$comment": "In sheets, this property is terminal"
			},
			"nearbyLandmark": {
				"type": "string",
				"$comment": "In sheets, this property is gate"
			},
			"tags": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Tag",
					"properties": {
						"label": { "type": "string" },
						"isHidden": {
							"type": "boolean",
							"default": false
						}
					},
					"required": ["label"],
					"constructorParameterOrder": ["label"],
					"additionalProperties": false
				}
			},
			"links": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Link",
					"properties": {
						"label": { "type": "string" },
						"type": {
							"type": "string",
							"enum": [
								"primary",
								"menu",
								"shop",
								"parking",
								"mobileordering"
							]
						},
						"uri": {
							"type": "string",
							"format": "uri-reference"
						}
					},
					"required": ["uri"],
					"constructorParameterOrder": [
						"uri",
						"type",
						"label"
					],
					"additionalProperties": false
				},
				"$comment": "could contain primary, menu, shop, parking, and mobileordering"
			},
			"searchTerms": {
				"type": "array",
				"items": {
					"type": "object",
					"title": "Search Term",
					"properties": {
						"type": {
							"type": "string",
							"enum": [
								"airline",
								"category",
								"custom"
							]
						},
						"value": { "type": "string" }
					},
					"required": ["type", "value"],
					"constructorParameterOrder": ["type", "value"],
					"additionalProperties": false
				},
				"$comment": "This should contain airline, categorySearch, and customSearch from google sheets"
			},
			"dynamicIntegration": {
				"title": "Dynamic Integration",
				"oneOf": [
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["open-closed-poi"]
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" }
						},
						"required": [
							"timeToLive",
							"clientId",
							"integrationType"
						],
						"additionalProperties": false
					},
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["nav"]
							},
							"default": { "type": "string" },
							"integrationUrl": {
								"type": "string",
								"format": "uri-reference"
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" },
							"property": { "type": "string" },
							"resultId": { "type": "string" },
							"checkpointId": { "type": "string" }
						},
						"required": ["timeToLive", "default"],
						"additionalProperties": false
					},
					{
						"type": "object",
						"properties": {
							"integrationType": {
								"type": "string",
								"enum": ["parking"]
							},
							"default": { "type": "string" },
							"integrationUrl": {
								"type": "string",
								"format": "uri-reference"
							},
							"timeToLive": { "type": "number" },
							"clientId": { "type": "string" },
							"property": { "type": "string" },
							"resultId": { "type": "string" },
							"lot": { "type": "string" }
						},
						"required": ["timeToLive", "default"],
						"additionalProperties": false
					}
				]
			}
		},
		"required": ["id", "entityId"],
		"constructorParameterOrder": [
			"id",
			"entityId",
			"venueId",
			"name",
			"description",
			"nearbySection",
			"nearbyLandmark",
			"tags",
			"links",
			"searchTerms",
			"dynamicIntegration"
		]
	}, {
		"properties": { "checkin": { "type": "string" } },
		"required": []
	}]
};

//#endregion
//#region src/object-model/proposed/feature.json
var feature_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/feature.json",
	title: "Feature",
	objectType: "Feature",
	type: "object",
	definitions: { "featureType": {
		"type": "string",
		"enum": [
			"text",
			"badge",
			"logo",
			"line",
			"polygon"
		]
	} },
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"geometry": {
			"title": "AI coordinate-system Geometry",
			"definitions": {
				"geometryType": {
					"type": "string",
					"enum": [
						"Point",
						"LineString",
						"Polygon"
					]
				},
				"aiPoint": {
					"type": "array",
					"items": { "type": "number" },
					"minItems": 2,
					"maxItems": 6
				},
				"aiLine": {
					"type": "array",
					"items": {
						"type": "array",
						"items": { "type": "number" },
						"minItems": 2,
						"maxItems": 6
					},
					"minItems": 2
				},
				"aiPolygon": {
					"type": "array",
					"items": {
						"type": "array",
						"items": {
							"type": "array",
							"items": { "type": "number" },
							"minItems": 2,
							"maxItems": 6
						},
						"minItems": 2
					},
					"minItems": 1
				},
				"aiCoordinates": { "oneOf": [
					{
						"type": "array",
						"items": { "type": "number" },
						"minItems": 2,
						"maxItems": 6
					},
					{
						"type": "array",
						"items": {
							"type": "array",
							"items": { "type": "number" },
							"minItems": 2,
							"maxItems": 6
						},
						"minItems": 2
					},
					{
						"type": "array",
						"items": {
							"type": "array",
							"items": {
								"type": "array",
								"items": { "type": "number" },
								"minItems": 2,
								"maxItems": 6
							},
							"minItems": 2
						},
						"minItems": 1
					}
				] },
				"aiGeometry": {
					"type": "object",
					"properties": {
						"type": {
							"type": "string",
							"enum": [
								"Point",
								"LineString",
								"Polygon"
							]
						},
						"coordinates": { "oneOf": [
							{
								"type": "array",
								"items": { "type": "number" },
								"minItems": 2,
								"maxItems": 6
							},
							{
								"type": "array",
								"items": {
									"type": "array",
									"items": { "type": "number" },
									"minItems": 2,
									"maxItems": 6
								},
								"minItems": 2
							},
							{
								"type": "array",
								"items": {
									"type": "array",
									"items": {
										"type": "array",
										"items": { "type": "number" },
										"minItems": 2,
										"maxItems": 6
									},
									"minItems": 2
								},
								"minItems": 1
							}
						] }
					}
				}
			}
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"idIndexWithinVenue": {
			"type": "number",
			"minimum": 1
		},
		"category": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"featureType": {
			"type": "string",
			"enum": [
				"text",
				"badge",
				"logo",
				"line",
				"polygon"
			]
		},
		"orderingWithinParent": {
			"type": "string",
			"pattern": "^.[0-9]+$"
		},
		"isClickable": {
			"type": "boolean",
			"default": true,
			"$comment": "When false a user-click should *not* select the related Entity as with some parking markers"
		},
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"structureId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
		}] },
		"logo": { "type": "string" },
		"fontName": { "type": "string" },
		"fontSize": { "type": "number" },
		"text": { "type": "string" },
		"rotation": { "type": "number" },
		"scaling": { "type": "number" },
		"fillColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$"
		},
		"strokeColor": {
			"type": "string",
			"pattern": "^#[0-9a-fA-F]{6}$"
		},
		"strokeWidth": {
			"type": "number",
			"minimum": 0
		},
		"strokeAlign": {
			"type": "string",
			"enum": [
				"inside",
				"center",
				"outside"
			]
		},
		"textAlign": {
			"type": "string",
			"enum": [
				"left",
				"center",
				"right"
			]
		},
		"dashCount": { "type": "number" },
		"dashValues": {
			"type": "array",
			"items": { "type": "number" }
		},
		"customId": {
			"type": "string",
			"$comment": "Provided by our customers; field has no specific meaning to us"
		},
		"isTouch": {
			"type": "boolean",
			"$comment": "For PIXI only: migrate to FeatureDeprecated?"
		},
		"subid": { "type": "string" }
	},
	required: [
		"id",
		"geometry",
		"venueId",
		"category",
		"featureType",
		"orderingWithinParent",
		"isClickable"
	],
	constructorParameterOrder: [
		"id",
		"geometry",
		"venueId",
		"category",
		"featureType",
		"orderingWithinParent",
		"isClickable",
		"idIndexWithinVenue",
		"customId",
		"fillColor",
		"strokeAlign",
		"strokeColor",
		"strokeWidth",
		"fontName",
		"fontSize",
		"textAlign",
		"fillColor",
		"strokeAlign",
		"strokeColor",
		"strokeWidth",
		"levelId",
		"logo",
		"text",
		"rotation",
		"scaling",
		"dashCount",
		"dashValues",
		"subid",
		"isTouch",
		"structureId"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/occupancy-history.json
var occupancy_history_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/occupancy-history.json",
	title: "Occupancy History",
	objectType: "OccupancyHistory",
	type: "object",
	definitions: { "state-machine-state": {
		"type": "string",
		"enum": [
			"unoccupied",
			"open",
			"temporarily-closed",
			"permanently-closed"
		]
	} },
	properties: {
		"id": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"featureId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"entityId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		}
	},
	required: [
		"id",
		"venueId",
		"featureId"
	],
	constructorParameterOrder: [
		"id",
		"venueId",
		"featureId",
		"entityId"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/tag.json
var tag_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/tag.json",
	type: "object",
	title: "Tag",
	properties: {
		"label": { "type": "string" },
		"isHidden": {
			"type": "boolean",
			"default": false
		}
	},
	required: ["label"],
	constructorParameterOrder: ["label"],
	additionalProperties: false
};

//#endregion
//#region src/object-model/link.json
var link_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/link.json",
	type: "object",
	title: "Link",
	properties: {
		"label": { "type": "string" },
		"type": {
			"type": "string",
			"enum": [
				"primary",
				"menu",
				"shop",
				"parking",
				"mobileordering"
			]
		},
		"uri": {
			"type": "string",
			"format": "uri-reference"
		}
	},
	required: ["uri"],
	constructorParameterOrder: [
		"uri",
		"type",
		"label"
	],
	additionalProperties: false
};

//#endregion
//#region src/object-model/search-term.json
var search_term_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/search-term.json",
	type: "object",
	title: "Search Term",
	properties: {
		"type": {
			"type": "string",
			"enum": [
				"airline",
				"category",
				"custom"
			]
		},
		"value": { "type": "string" }
	},
	required: ["type", "value"],
	constructorParameterOrder: ["type", "value"],
	additionalProperties: false
};

//#endregion
//#region src/object-model/capacity.json
var capacity_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/capacity.json",
	type: "array",
	title: "Capacity",
	minItems: 1,
	maxItems: 2,
	uniqueItems: true,
	items: {
		"type": "integer",
		"minimum": 0,
		"exclusiveMinimum": 0,
		"$comment": "This seems redundant, but is here because of the object store implementation.  It has an odd mishmash of schema04 and schema07."
	},
	description: "The unitless capacity of a room.  Numbers are inclusive",
	constructorParameterOrder: [],
	additionalProperties: false
};

//#endregion
//#region src/object-model/dynamic-integration.json
var dynamic_integration_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/dynamic-integration.json",
	title: "Dynamic Integration",
	oneOf: [
		{
			"type": "object",
			"properties": {
				"integrationType": {
					"type": "string",
					"enum": ["open-closed-poi"]
				},
				"timeToLive": { "type": "number" },
				"clientId": { "type": "string" }
			},
			"required": [
				"timeToLive",
				"clientId",
				"integrationType"
			],
			"additionalProperties": false
		},
		{
			"type": "object",
			"properties": {
				"integrationType": {
					"type": "string",
					"enum": ["nav"]
				},
				"default": { "type": "string" },
				"integrationUrl": {
					"type": "string",
					"format": "uri-reference"
				},
				"timeToLive": { "type": "number" },
				"clientId": { "type": "string" },
				"property": { "type": "string" },
				"resultId": { "type": "string" },
				"checkpointId": { "type": "string" }
			},
			"required": ["timeToLive", "default"],
			"additionalProperties": false
		},
		{
			"type": "object",
			"properties": {
				"integrationType": {
					"type": "string",
					"enum": ["parking"]
				},
				"default": { "type": "string" },
				"integrationUrl": {
					"type": "string",
					"format": "uri-reference"
				},
				"timeToLive": { "type": "number" },
				"clientId": { "type": "string" },
				"property": { "type": "string" },
				"resultId": { "type": "string" },
				"lot": { "type": "string" }
			},
			"required": ["timeToLive", "default"],
			"additionalProperties": false
		}
	]
};

//#endregion
//#region src/object-model/proposed/proposed-string-patterns.json
var proposed_string_patterns_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/proposed-string-patterns.json",
	title: "String Patterns",
	definitions: {
		"featureId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"boundsId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"geofenceId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"labelId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"sectionId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"navigationLineId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"aiJsonId": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueAddressUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"structureUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"levelUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"walkwayUuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"ordering": {
			"type": "string",
			"pattern": "^.[0-9]+$"
		},
		"uri": {
			"type": "string",
			"format": "uri"
		},
		"aiNamePath": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9_! -]+$"
		},
		"uuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		}
	}
};

//#endregion
//#region src/object-model/index.js
var object_model_exports = /* @__PURE__ */ __exportAll({
	A13468QJ9MG74MEntityOverride: () => A13468QJ9MG74M_entity_override_default,
	A1B4XSN6HDHIR2EntityOverride: () => A1B4XSN6HDHIR2_entity_override_default,
	A1GHMDY8PFDNUAEntityOverride: () => A1GHMDY8PFDNUA_entity_override_default,
	Address: () => address_default,
	AiJsonReference: () => AiJsonReference_default,
	AugmentedReality: () => augmented_reality_default,
	BeaconRegions: () => beacon_regions_default,
	BoundingBox: () => bounding_box_default$1,
	BoundsPolygon: () => bounds_polygon_default$1,
	Capacity: () => capacity_default,
	Category: () => category_default,
	ContactInformation: () => contact_information_default,
	DynamicIntegration: () => dynamic_integration_default,
	DynamicPOIs: () => dynamic_pois_default,
	Entity: () => entity_default$1,
	EntityDeprecated: () => entity_deprecated_default,
	EntityOverride: () => entity_override_default,
	Feature: () => feature_default,
	FixedGeometry: () => fixed_geometry_default,
	Grab: () => grab_default,
	Image: () => image_default,
	LatLng: () => latlng_default$1,
	LatLngArray: () => latlng_array_default$1,
	Level: () => level_default$1,
	Link: () => link_default,
	NavGraphReference: () => navGraphReference_default,
	OccupancyHistory: () => occupancy_history_default,
	Ordinal: () => ordinal_default$1,
	Places: () => places_default,
	PoiSheetReference: () => poiSheetReference_default,
	ProposedStringPatterns: () => proposed_string_patterns_default,
	Rectangle: () => rectangle_default,
	SearchTerm: () => search_term_default,
	StringPatterns: () => string_patterns_default$2,
	Structure: () => structure_default$1,
	Tag: () => tag_default,
	Theme: () => theme_default,
	UtmData: () => utm_data_default,
	Venue: () => venue_default$1,
	VenueDeprecated: () => venue_deprecated_default,
	WaitingLaneGroups: () => waiting_lane_groups_default,
	WayfindingSegment: () => wayfinding_segment_default$1
});

//#endregion
//#region src/svg-command-schemas/svg-circle-command-schema.json
var svg_circle_command_schema_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-svg.com/schemas/svg-circle-command.json",
	title: "SVG Circle",
	type: "object",
	properties: {
		"cx": { "$ref": "string-patterns.json#/definitions/numberString" },
		"cy": { "$ref": "string-patterns.json#/definitions/numberString" },
		"r": { "$ref": "string-patterns.json#/definitions/numberString" },
		"fill": { "type": "string" }
	},
	required: [
		"cx",
		"cy",
		"r",
		"fill"
	],
	additionalProperties: false
};

//#endregion
//#region src/svg-command-schemas/svg-line-command-schema.json
var svg_line_command_schema_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-svg.com/schemas/svg-line-command.json",
	title: "SVG Line",
	type: "object",
	properties: {
		"x1": { "$ref": "string-patterns.json#/definitions/numberString" },
		"y1": { "$ref": "string-patterns.json#/definitions/numberString" },
		"x2": { "$ref": "string-patterns.json#/definitions/numberString" },
		"y2": { "$ref": "string-patterns.json#/definitions/numberString" },
		"class": { "type": "string" },
		"style": {
			"type": "string",
			"default": ""
		}
	},
	required: [
		"x1",
		"y1",
		"x2",
		"y2"
	],
	additionalProperties: false
};

//#endregion
//#region src/svg-command-schemas/svg-rect-command-schema.json
var svg_rect_command_schema_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-svg.com/schemas/svg-rect-command.json",
	title: "SVG Rectangle",
	type: "object",
	properties: {
		"x": {
			"$ref": "string-patterns.json#/definitions/numberString",
			"default": "0"
		},
		"y": {
			"$ref": "string-patterns.json#/definitions/numberString",
			"default": "0"
		},
		"rx": {
			"$ref": "string-patterns.json#/definitions/numberString",
			"default": "0"
		},
		"ry": {
			"$ref": "string-patterns.json#/definitions/numberString",
			"default": "0"
		},
		"width": { "$ref": "string-patterns.json#/definitions/numberString" },
		"height": { "$ref": "string-patterns.json#/definitions/numberString" },
		"id": { "type": "string" },
		"fill": { "type": "string" },
		"class": { "type": "string" },
		"style": {
			"type": "string",
			"default": ""
		},
		"transform": { "type": "string" }
	},
	required: ["width", "height"],
	additionalProperties: false
};

//#endregion
//#region src/svg-command-schemas/string-patterns.json
var string_patterns_default$1 = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-svg.com/schemas/string-patterns.json",
	title: "String Patterns",
	definitions: { "numberString": {
		"type": "string",
		"pattern": "^[-+]?(?:\\d*\\.\\d+|\\d+\\.?)$"
	} }
};

//#endregion
//#region src/svg-command-schemas/index.js
var svg_command_schemas_exports = /* @__PURE__ */ __exportAll({
	StringPatterns: () => string_patterns_default$1,
	SvgCircleCommand: () => svg_circle_command_schema_default,
	SvgLineCommand: () => svg_line_command_schema_default,
	SvgRectCommand: () => svg_rect_command_schema_default
});

//#endregion
//#region src/vms/string-patterns.json
var string_patterns_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/string-patterns.json",
	title: "String Patterns",
	definitions: {
		"align": {
			"type": "string",
			"enum": [
				"left",
				"center",
				"right"
			]
		},
		"categoryName": {
			"type": "string",
			"pattern": "^[a-z.]+$"
		},
		"color": {
			"type": "string",
			"pattern": "^#[0-9a-f]{6}$"
		},
		"dateTime": {
			"type": "string",
			"format": "date-time"
		},
		"entityId": {
			"type": "string",
			"pattern": "^[0-9]+$"
		},
		"featureId": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"hours": { "anyOf": [
			{ "$ref": "#/definitions/hoursOneDay" },
			{ "$ref": "#/definitions/hoursDayRange" },
			{ "$ref": "#/definitions/hoursDayRangeAndDay" },
			{ "$ref": "#/definitions/hoursDayRangeAndDayRange" },
			{ "$ref": "#/definitions/hoursDayRangeAndDayAndDay" }
		] },
		"hoursOneDay": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRange": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRangeAndDay": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRangeAndDayRange": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"hoursDayRangeAndDayAndDay": {
			"type": "string",
			"pattern": "^ *(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9]; *(Mo|Tu|We|Th|Fr|Sa|Su) *[0-2][0-9]:[0-5][0-9]-[0-2][0-9]:[0-5][0-9] *$"
		},
		"levelId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+-[a-zA-Z0-9:]+$"
		}] },
		"numberString": {
			"type": "string",
			"pattern": "^[-+]?(?:\\d*\\.\\d+|\\d+\\.?)$"
		},
		"ordinalId": {
			"type": "string",
			"pattern": "^ordinal: -*[0-9]+$"
		},
		"strokeAlign": {
			"type": "string",
			"enum": [
				"inside",
				"center",
				"outside"
			]
		},
		"structureId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+-[a-zA-Z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID-[a-zA-Z0-9:]+$"
		}] },
		"subid": {
			"type": "string",
			"pattern": "^[0-9]+[a-z]$"
		},
		"utmZone": {
			"type": "string",
			"pattern": "[0-9]{2}s?"
		},
		"uuid": {
			"type": "string",
			"pattern": "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$"
		},
		"venueId": { "oneOf": [{
			"type": "string",
			"pattern": "^[a-z0-9:]+$"
		}, {
			"type": "string",
			"pattern": "^VENUEID$"
		}] },
		"version": {
			"type": "string",
			"pattern": "20[0-9]{2}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}$"
		},
		"vmsUuid": {
			"type": "string",
			"pattern": "^[a-zA-Z0-9]+_[0-9]+$"
		}
	}
};

//#endregion
//#region src/vms/latlng.json
var latlng_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/latlng.json",
	type: "object",
	title: "LatLng",
	properties: {
		"lat": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"lng": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		}
	},
	required: ["lat", "lng"],
	constructorParameterOrder: ["lat", "lng"],
	additionalProperties: false
};

//#endregion
//#region src/vms/latlng-array.json
var latlng_array_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/latlng-array.json",
	title: "LatLng Array",
	type: "array",
	items: {
		"type": "number",
		"minimum": -180,
		"maximum": 180
	},
	minItems: 2,
	maxItems: 2
};

//#endregion
//#region src/vms/bounding-box.json
var bounding_box_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/bounding-box.json",
	type: "object",
	title: "Bounding Box",
	properties: {
		"n": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"e": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"s": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"w": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		}
	},
	required: [
		"n",
		"e",
		"s",
		"w"
	],
	constructorParameterOrder: [
		"n",
		"e",
		"s",
		"w"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/bounds-polygon.json
var bounds_polygon_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/bounds-polygon.json",
	type: "array",
	title: "Bounds Polygon",
	items: { "$ref": "latlng-array.json" }
};

//#endregion
//#region src/vms/level.json
var level_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/level.json",
	type: "object",
	title: "Level",
	properties: {
		"venueId": { "$ref": "string-patterns.json#/definitions/venueId" },
		"structureId": { "$ref": "string-patterns.json#/definitions/structureId" },
		"ordinalId": { "$ref": "string-patterns.json#/definitions/ordinalId" },
		"id": { "$ref": "string-patterns.json#/definitions/levelId" },
		"name": { "type": "string" },
		"isDefaultForStructure": { "type": "boolean" },
		"details": { "type": "string" },
		"boundingBox": { "$ref": "bounding-box.json" },
		"clFloor": { "type": "number" }
	},
	required: [
		"venueId",
		"structureId",
		"ordinalId",
		"id",
		"name",
		"details",
		"isDefaultForStructure",
		"boundingBox"
	],
	constructorParameterOrder: [
		"venueId",
		"structureId",
		"ordinalId",
		"id",
		"name",
		"details",
		"isDefaultForStructure",
		"boundingBox",
		"clFloor"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/ordinal.json
var ordinal_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/ordinal.json",
	type: "object",
	title: "Ordinal",
	properties: {
		"venueId": { "$ref": "string-patterns.json#/definitions/venueId" },
		"id": { "$ref": "string-patterns.json#/definitions/ordinalId" },
		"ordinalNumber": { "type": "number" },
		"isDefaultForVenue": { "type": "boolean" }
	},
	required: [
		"venueId",
		"id",
		"ordinalNumber",
		"isDefaultForVenue"
	],
	constructorParameterOrder: [
		"venueId",
		"id",
		"ordinalNumber",
		"isDefaultForVenue"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/structure.json
var structure_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/structure.json",
	type: "object",
	title: "Structure",
	properties: {
		"id": { "$ref": "string-patterns.json#/definitions/structureId" },
		"name": { "type": "string" },
		"shouldDisplay": { "type": "boolean" },
		"isDefaultForVenue": { "type": "boolean" },
		"venueId": { "$ref": "string-patterns.json#/definitions/venueId" },
		"boundingBox": { "$ref": "bounding-box.json" }
	},
	required: [
		"id",
		"name",
		"shouldDisplay",
		"isDefaultForVenue",
		"venueId",
		"boundingBox"
	],
	constructorParameterOrder: [
		"venueId",
		"id",
		"name",
		"shouldDisplay",
		"isDefaultForVenue",
		"boundingBox"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/venue.json
var venue_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/venue.json",
	type: "object",
	title: "Venue",
	properties: {
		"assetFormatVersion": { "type": "string" },
		"boundingBox": { "$ref": "bounding-box.json" },
		"id": { "$ref": "string-patterns.json#/definitions/venueId" },
		"name": { "type": "string" },
		"venueCategory": { "type": "string" },
		"venueCenter": { "$ref": "latlng-array.json" }
	},
	required: [
		"assetFormatVersion",
		"boundingBox",
		"id",
		"name",
		"venueCategory",
		"venueCenter"
	],
	constructorParameterOrder: [
		"assetFormatVersion",
		"boundingBox",
		"id",
		"name",
		"venueCategory",
		"venueCenter"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/venue-summary.json
var venue_summary_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/venue-summary.json",
	type: "object",
	title: "Venue Summary",
	properties: {
		"code": { "type": "string" },
		"id": { "$ref": "string-patterns.json#/definitions/venueId" },
		"name": { "type": "string" },
		"locale": { "type": "string" }
	},
	required: [
		"code",
		"id",
		"name",
		"locale"
	],
	constructorParameterOrder: [
		"code",
		"id",
		"name",
		"locale"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/map-note-comment.json
var map_note_comment_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	definitions: {
		"item": {
			"type": "object",
			"oneOf": [{ "$ref": "#/definitions/comment" }, { "$ref": "#/definitions/history" }]
		},
		"comment": {
			"type": "object",
			"properties": {
				"id": {
					"type": "number",
					"minimum": 0
				},
				"type": {
					"type": "string",
					"enum": ["comment"]
				},
				"uploads": {
					"type": "array",
					"items": {
						"type": "string",
						"format": "uri"
					}
				},
				"body": { "type": "string" },
				"createdAt": { "$ref": "string-patterns.json#/definitions/dateTime" },
				"createdBy": { "$ref": "#/definitions/createdBy" }
			},
			"required": [
				"id",
				"type",
				"createdAt",
				"createdBy"
			],
			"additionalProperties": false
		},
		"history": {
			"type": "object",
			"properties": {
				"type": {
					"type": "string",
					"enum": ["history"]
				},
				"createdAt": { "$ref": "string-patterns.json#/definitions/dateTime" },
				"fieldName": {
					"type": "string",
					"enum": [
						"subject",
						"noteType",
						"noteStatus",
						"latitude",
						"longitude"
					]
				},
				"previousValue": { "oneOf": [
					{ "type": "string" },
					{ "type": "number" },
					{ "type": "object" }
				] },
				"newValue": { "oneOf": [
					{ "type": "string" },
					{ "type": "number" },
					{ "type": "object" }
				] }
			},
			"required": [
				"type",
				"createdAt",
				"newValue",
				"previousValue",
				"fieldName"
			],
			"additionalProperties": false
		},
		"createdBy": {
			"type": "object",
			"properties": { "email": {
				"type": "string",
				"format": "email"
			} },
			"required": ["email"],
			"additionalProperties": false
		}
	},
	$id: "http://locuslabs-vms.com/schemas/map-note-comment.json",
	title: "Map Note Comment",
	$ref: "#/definitions/item",
	additionalFields: false
};

//#endregion
//#region src/vms/map-note-filter.json
var map_note_filter_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	definitions: { "type": {
		"type": "object",
		"properties": {
			"noType": { "type": "boolean" },
			"mapRequest": { "type": "boolean" },
			"fieldVerify": { "type": "boolean" }
		},
		"required": [
			"noType",
			"mapRequest",
			"fieldVerify"
		],
		"additionalFields": false
	} },
	$id: "http://locuslabs-vms.com/schemas/map-note-filter.json",
	title: "Map Note Filter",
	type: "object",
	properties: {
		"status": {
			"type": "string",
			"enum": [
				"open",
				"resolved",
				"all"
			]
		},
		"type": { "$ref": "#/definitions/type" }
	},
	required: ["status", "type"],
	additionalFields: false
};

//#endregion
//#region src/vms/map-note.json
var map_note_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	definitions: { "location": {
		"type": "object",
		"properties": {
			"venueId": { "$ref": "string-patterns.json#/definitions/venueId" },
			"ordinalId": { "$ref": "string-patterns.json#/definitions/ordinalId" },
			"latitude": { "type": "number" },
			"longitude": { "type": "number" },
			"levelId": { "$ref": "string-patterns.json#/definitions/levelId" },
			"structureId": { "$ref": "string-patterns.json#/definitions/structureId" }
		},
		"required": [
			"venueId",
			"ordinalId",
			"latitude",
			"longitude"
		],
		"additionalFields": false
	} },
	$id: "http://locuslabs-vms.com/schemas/map-note.json",
	title: "Map Note",
	type: "object",
	properties: {
		"subject": {
			"type": "string",
			"minLength": 1
		},
		"noteStatus": {
			"type": "string",
			"enum": ["open", "resolved"]
		},
		"description": { "type": "string" },
		"noteType": {
			"type": "string",
			"enum": [
				"noType",
				"fieldVerify",
				"mapRequest"
			]
		},
		"location": { "$ref": "#/definitions/location" }
	},
	required: [
		"subject",
		"noteStatus",
		"description",
		"noteType",
		"location"
	],
	additionalFields: false
};

//#endregion
//#region src/vms/wayfinding-segment.json
var wayfinding_segment_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-vms.com/schemas/wayfindingSegment.json",
	type: "object",
	title: "Wayfinding Segment",
	definitions: {
		"lng": {
			"type": "number",
			"minimum": -180,
			"maximum": 180
		},
		"lat": {
			"type": "number",
			"minimum": -90,
			"maximum": 90
		},
		"segmentType": {
			"type": "string",
			"enum": [
				"walk",
				"security",
				"escalator"
			]
		},
		"lngLatArray": {
			"type": "array",
			"items": [{ "$ref": "#/definitions/lng" }, { "$ref": "#/definitions/lat" }]
		},
		"lineString": {
			"type": "array",
			"items": { "$ref": "#/definitions/lngLatArray" }
		},
		"badge": {
			"type": "object",
			"properties": {
				"entityId": { "$ref": "string-patterns.json#/definitions/entityId" },
				"canonicalName": { "$ref": "string-patterns.json#/definitions/categoryName" },
				"coordinates": { "$ref": "#/definitions/lngLatArray" }
			},
			"required": [
				"entityId",
				"canonicalName",
				"coordinates"
			],
			"additionalProperties": false
		}
	},
	properties: {
		"levelId": { "$ref": "string-patterns.json#/definitions/levelId" },
		"ordinalId": { "$ref": "string-patterns.json#/definitions/ordinalId" },
		"venueId": { "$ref": "string-patterns.json#/definitions/venueId" },
		"coordinates": { "$ref": "#/definitions/lineString" },
		"segmentType": { "$ref": "#/definitions/segmentType" },
		"badges": {
			"type": "array",
			"items": { "$ref": "#/definitions/badge" }
		}
	},
	required: [
		"levelId",
		"ordinalId",
		"venueId",
		"segmentType",
		"coordinates"
	],
	additionalProperties: false
};

//#endregion
//#region src/vms/index.js
var vms_exports = /* @__PURE__ */ __exportAll({
	BoundingBox: () => bounding_box_default,
	BoundsPolygon: () => bounds_polygon_default,
	LatLng: () => latlng_default,
	LatLngArray: () => latlng_array_default,
	Level: () => level_default,
	MapNote: () => map_note_default,
	MapNoteComment: () => map_note_comment_default,
	MapNoteFilter: () => map_note_filter_default,
	Ordinal: () => ordinal_default,
	StringPatterns: () => string_patterns_default,
	Structure: () => structure_default,
	Venue: () => venue_default,
	VenueSummary: () => venue_summary_default,
	WayfindingSegment: () => wayfinding_segment_default
});

//#endregion
//#region src/cooked-assets/pois-3.0-schema.js
const namePattern = `[a-zA-Z0-9@& .'-]+`;
const integerPattern = `\\d+`;
const categoryNamePattern = "[a-z.]+";
const floorIdPattern = "[a-z0-9]+-[a-z0-9]+-[a-z0-9]+";
const categoryPattern = `category:${categoryNamePattern}`;
const gatePattern = `gate:${namePattern}`;
const descriptionPattern = `[a-zA-Z0-9@& .,'-]+`;
const imagePattern = "[A-Z0-9_]+.jpg";
const keywordPattern = `${categoryPattern}$|^${gatePattern}$|^${namePattern}`;
const weekdayPattern = "(Mo|Tu|We|Th|Fr|Sa|Su)";
const weekdayRangePattern = `${weekdayPattern}-${weekdayPattern}`;
const hourPattern = "[0-2][0-9]";
const minutePattern = "[0-5][0-9]";
const operationHoursPattern = `${weekdayRangePattern} ${`${hourPattern}:${minutePattern}-${hourPattern}:${minutePattern}`}`;
const stringSchema = (pattern, description$20) => ({
	type: "string",
	pattern: `^${pattern}$`,
	description: description$20
});
const numberSchema = (min, max, description$20) => ({
	type: "number",
	min,
	max,
	description: description$20
});
const booleanSchema = (description$20) => ({
	type: "boolean",
	description: description$20
});
const objectSchema = (properties$76) => ({
	type: "object",
	properties: properties$76,
	required: Object.keys(properties$76),
	additionalProperties: false
});
const arraySchema = (itemSchema, description$20) => ({
	type: "array",
	minItems: 1,
	items: itemSchema,
	description: description$20
});
const CategorySchema = stringSchema(categoryNamePattern, "The category of the POI");
const FloorIdSchema = stringSchema(floorIdPattern, "The venue-structure-level floorId for this POI");
const ImageSchema = stringSchema(imagePattern, "URL of an image");
const IsAfterSecuritySchema = booleanSchema("Do you have to pass through security to get to this POI?");
const IsNavigableSchema = booleanSchema("Is it possible to navigate to this POI?");
const KeywordIsDisplayedSchema = booleanSchema("Should this keyword should be displayed in the POI window?");
const KeywordIsUserSearchableSchema = booleanSchema("Can the user search for this keyword?");
const KeywordNameSchema = stringSchema(keywordPattern, "The search term or tag for this keyword");
const LatitudeSchema = numberSchema(-180, 180, "The latitude of (the center of) this POI");
const LinkTypeSchema = {
	type: "string",
	enum: ["primary"]
};
const LinkUrlSchema = {
	type: "string",
	format: "uri-reference"
};
const LongitudeSchema = numberSchema(-180, 180, "The longitude of (the center of) this POI");
const NearbyLandmarkSchema = stringSchema(namePattern, "A user-friencly familiar landmark near this POI");
const OperationHoursSchema = stringSchema(operationHoursPattern, "AVF string format for operation hours");
const PoiDescriptionSchema = stringSchema(descriptionPattern, "The description of the POI for the POI window");
const PoiIdSchema = stringSchema(integerPattern, "The id of the POI, unique within a given venue");
const PoiNameSchema = stringSchema(namePattern, "The untranslated name of the POI");
const ZoomRadiusSchema = stringSchema(integerPattern, "The radius to zoom to when viewing this POI");
const KeywordSchema = objectSchema({
	name: KeywordNameSchema,
	isDisplayed: KeywordIsDisplayedSchema,
	isUserSearchable: KeywordIsUserSearchableSchema
});
const PrimaryLinkSchema = objectSchema({
	type: LinkTypeSchema,
	url: LinkUrlSchema
});
const PositionSchema = objectSchema({
	floorId: FloorIdSchema,
	latitude: LatitudeSchema,
	longitude: LongitudeSchema
});
const KeywordsSchema = arraySchema(KeywordSchema, "An array of keywords");
const ImagesSchema = arraySchema(ImageSchema, "An array of images");
const LinksSchema = arraySchema({ anyOf: [PrimaryLinkSchema] }, "An array of links");
const CookedPoiSchema = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-cooked-files.com/schemas/cooked-poi.json",
	title: "Cooked Poi",
	type: "object",
	properties: {
		category: CategorySchema,
		isAfterSecurity: IsAfterSecuritySchema,
		isNavigable: IsNavigableSchema,
		keywords: KeywordsSchema,
		name: PoiNameSchema,
		nearbyLandmark: NearbyLandmarkSchema,
		poiId: PoiIdSchema,
		position: PositionSchema,
		zoomRadius: ZoomRadiusSchema,
		description: PoiDescriptionSchema,
		images: ImagesSchema,
		links: LinksSchema,
		operationHours: OperationHoursSchema
	},
	required: [
		"category",
		"isAfterSecurity",
		"isNavigable",
		"keywords",
		"name",
		"nearbyLandmark",
		"poiId",
		"position"
	],
	additionalProperties: false
};
const CookedPoisFileSchema = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-cooked-files.com/schemas/cooked-pois.json",
	title: "Cooked Pois",
	type: "object",
	patternProperties: { [`^${integerPattern}$`]: CookedPoiSchema },
	additionalProperties: false
};
var pois_3_0_schema_default = CookedPoisFileSchema;

//#endregion
//#region src/cooked-assets/proposed/entity.json
var entity_default = {
	$schema: "http://json-schema.org/draft-07/schema#",
	$id: "http://locuslabs-object-model.com/schemas/poi.json",
	title: "Point Of Interest",
	type: "object",
	definitions: { "capacity": {
		"type": "array",
		"title": "Capacity",
		"minItems": 1,
		"maxItems": 2,
		"uniqueItems": true,
		"items": {
			"type": "integer",
			"minimum": 0,
			"exclusiveMinimum": 0,
			"$comment": "This seems redundant, but is here because of the object store implementation.  It has an odd mishmash of schema04 and schema07."
		},
		"description": "The unitless capacity of a room.  Numbers are inclusive",
		"constructorParameterOrder": [],
		"additionalProperties": false
	} },
	properties: {
		"poiId": {
			"type": "string",
			"$comment": "This property is unchanged."
		},
		"name": {
			"type": "string",
			"$comment": "This property is unchanged."
		},
		"mapLabel": {
			"type": "string",
			"description": "optional name to be used if provided instead of 'name' for map labels",
			"$comment": "This property is new"
		},
		"category": {
			"type": "string",
			"pattern": "^[a-z.]+$",
			"examples": ["shop", "eat.coffee"],
			"$comment": "This property is unchanged."
		},
		"description": {
			"type": "string",
			"description": "A freeform text field to serve as a human readable description of the POI",
			"$comment": "This property is unchanged."
		},
		"phone": {
			"type": "string",
			"$comment": "This property is unchanged."
		},
		"images": {
			"type": "array",
			"items": { "type": "string" },
			"$comment": "This property is a combination of 'image' and 'additionalImages'.  The value is a concatenation of image and additionalImages"
		},
		"operationHours": {
			"type": "string",
			"$comment": "This property was previously 'rawHours'"
		},
		"zoomRadius": {
			"type": "string",
			"$comment": "This property was previously 'radius'.  Its calculated from associated features."
		},
		"showWindow": {
			"type": "boolean",
			"description": "Defines if you should be able to open the POI window.",
			"$comment": "This was previously 'displayProperties.showWindow'"
		},
		"isNavigable": {
			"type": "boolean",
			"description": "Defines if you can navigate to this POI",
			"$comment": "This is a new property."
		},
		"isAfterSecurity": {
			"type": "boolean",
			"$comment": "This property was previously 'position.isAfterSecurity'"
		},
		"queue": { "properties": {
			"primaryQueueId": {
				"type": "string",
				"$comment": "This property was previously 'additionalAttributes.checkpointID"
			},
			"queueType": {
				"type": "string",
				"$comment": "This is technically a new attribute. Previously, it was implicitly defined by there either being a 'SecurityLane' or 'ImmigrationLane' additionalAttribute"
			},
			"queueSubtype": {
				"type": "string",
				"$comment": "This was previously 'additionalAttributes.SecurityLane' OR 'additionalAttributes.ImmigrationLane'",
				"description": "this depends on the category, currently one of general/tsapre/clear/priority for SecurityLane and resident/visitor/global for ImmigrationLane"
			},
			"defaultQueueTime": {
				"type": "integer",
				"$comment": "This was previously 'dynamicAttributes'/'nav.default'"
			}
		} },
		"amenities": {
			"type": "array",
			"items": { "type": "string" },
			"$comment": "This was previously 'additionalAttributes.amenity'"
		},
		"capacity": {
			"$ref": "#/definitions/capacity",
			"$comment": "This was previously 'additionalAttributes.capacity'"
		},
		"position": {
			"type": "object",
			"properties": {
				"floorId": {
					"type": "string",
					"pattern": "^[a-z0-9-]+$",
					"$comment": "This property is unchanged."
				},
				"latitude": {
					"type": "number",
					"$comment": "This property is unchanged."
				},
				"longitude": {
					"type": "number",
					"$comment": "This property is unchanged."
				}
			}
		},
		"nearbyLandmark": {
			"type": "string",
			"$comment": "This property was previously 'gate'"
		},
		"externalIds": {
			"type": "array",
			"items": {
				"type": "object",
				"properties": {
					"type": {
						"type": "string",
						"$comment": "This is technically a new property.  Its value comes from the property name that corresponds to an 'id' in additionalAttributes (e.g. checkin)"
					},
					"id": {
						"type": "string",
						"$comment": "This is the value from the above property"
					}
				}
			},
			"$comment": "This is an array representing what was previously multiple properties, including 'additionalAttributes.checkin', 'additionalAttributes.preferenceId', 'additionalAttributes.roomId', 'additionalAttributes.roomResourceName'. We determined this an array, if a customer might want to distribute these ids to all clients"
		},
		"links": {
			"$comment": "This is a combination of many properties, all of which defined some type of link.  There is one object for each URL in the poi file, and I (andrew) know of 4 types: primary (previously url and urlDisplay), menu (previously menu), shop (previously shop and shop-display), and grab (previously grabUrl)",
			"type": "array",
			"items": {
				"type": "object",
				"properties": {
					"label": {
						"type": "string",
						"description": "A human readable label for this link",
						"$comment": "Previously shop-name or urlDisplay"
					},
					"type": {
						"type": "string",
						"examples": [
							"primary",
							"menu",
							"shop",
							"grab"
						],
						"description": "The type of link.  Is it a menu? a grab integration? something else?"
					},
					"url": {
						"type": "string",
						"format": "uri-reference"
					}
				}
			}
		},
		"keywords": {
			"$comment": "This information is a combination of: 'tags', 'displayTags', and other custom searches from the search.json file",
			"type": "array",
			"items": {
				"type": "object",
				"properties": {
					"name": {
						"type": "string",
						"description": "The search term or tag"
					},
					"isDisplayed": {
						"type": "boolean",
						"default": "false",
						"description": "Defines if this keyword should be displayed in the poi window."
					},
					"isUserSearchable": {
						"type": "boolean",
						"default": "true",
						"description": "Defines if the keyword should be searchable by the user"
					}
				}
			}
		},
		"geofence": {
			"type": "string",
			"$comment": "This was previously additionalAttributes.geofence"
		},
		"additionalAttributes": {
			"$comment": "this is exposed to our customers as-is",
			"type": "object",
			"properties": {
				"logoUrl": {
					"type": "string",
					"$comment": "This was previously logo"
				},
				"journeyPlanner": {
					"type": "object",
					"properties": {
						"name": {
							"type": "string",
							"$comment": "This property was previously 'additionalAttributes.journeyPlanner'"
						},
						"pinned": {
							"type": "boolean",
							"$comment": "This property was previously 'additionalAttributes.pinned'"
						},
						"priority": {
							"type": "number",
							"$comment": "This property was previously 'additionalAttributes.priority'"
						}
					}
				}
			}
		},
		"dynamicIntegration": {
			"type": "object",
			"$comment": "WARNING: These properties are to be used only by the REST API and are subject to change",
			"properties": {
				"nav": {
					"$comment": "These properties were previously 'dynamicAttributes.nav.XXXXX'",
					"properties": {
						"url": { "type": "string" },
						"default": { "type": "string" },
						"timeToLive": { "type": "string" },
						"clientId": { "type": "string" },
						"property": { "type": "string" },
						"CheckpointID": { "type": "string" }
					}
				},
				"parking": {
					"$comment": "These properties were previously 'dynamicAttributes.parking.XXXXX'",
					"properties": {
						"default": { "type": "string" },
						"timeToLive": { "type": "string" },
						"url": { "type": "string" },
						"lot": { "type": "string" },
						"clientId": { "type": "string" }
					}
				}
			}
		}
	},
	required: []
};

//#endregion
//#region src/cooked-assets/index.js
var cooked_assets_exports = /* @__PURE__ */ __exportAll({
	CookedPoiSchema: () => entity_default,
	CookedPoisFileSchema: () => pois_3_0_schema_default
});

//#endregion
//#region src/common/index.js
var common_exports = /* @__PURE__ */ __exportAll({ numericProperties: () => numeric_properties_default });

//#endregion
//#region src/index.js
var src_default = {
	Ajv: ajv_extensions_exports,
	Ai: ai_json_schemas_exports,
	ApiMarketplace: api_marketplace_exports,
	FileSchemas: file_schemas_exports,
	Imdf: imdf_exports,
	ObjectModel: object_model_exports,
	SvgCommands: svg_command_schemas_exports,
	Vms: vms_exports,
	CookedAssets: cooked_assets_exports,
	Common: common_exports
};

//#endregion
export { ai_json_schemas_exports as Ai, ajv_extensions_exports as Ajv, api_marketplace_exports as ApiMarketplace, common_exports as Common, cooked_assets_exports as CookedAssets, file_schemas_exports as FileSchemas, imdf_exports as Imdf, object_model_exports as ObjectModel, svg_command_schemas_exports as SvgCommands, vms_exports as Vms, src_default as default };